import { apiClient, getAuthToken } from "./client";
import { compareInstantsAtMicrosecondPrecision, normalizeInstantToUtcMicroseconds } from "../lib/temporalInstant";
import type { JsonValue } from "./canonicalLineage";
import {
  ExportIntegrityError,
  exportBlob,
  hasExactMediaType,
  readBoundedResponseBytes,
  verifyJsonExportBytes,
  verifyOfflineHtmlBytes,
} from "./exportIntegrity";

export const PROOF_TRACE_CONTRACT_VERSION = "thread.proof-trace.v2" as const;
export const PROOF_TRACE_RESOURCE_LIMITS_VERSION = "thread.proof-trace-resource-limits.v1" as const;
export const PROOF_TRACE_MIN_SCHEMA_MIGRATION = "20260810210000" as const;
export const PROOF_TRACE_PATH = "/thread/v1/provenance/proof-trace" as const;
export const PROOF_TRACE_EXPORT_PATH = "/thread/v1/provenance/proof-trace/export" as const;
const PROOF_TRACE_MAX_HTML_BYTES = 33_554_432;

export const PROOF_TRACE_DEFAULT_LIMITS = {
  max_depth: 32,
  max_records: 500,
  max_paths: 5000,
  max_path_steps: 5000,
} as const;

export const PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS = {
  maximum_depth: 32,
  maximum_nodes: 4096,
  maximum_canonical_bytes: 262_144,
  maximum_aggregate_nodes: 32_768,
  maximum_aggregate_canonical_bytes: 2_097_152,
  maximum_document_canonical_bytes: 8_388_608,
} as const;

export interface ProofTraceArtifactVersionRef {
  entity_kind: "artifact_version";
  id: string;
}

export interface ProofTraceRequest {
  project_id: string;
  root_ref: ProofTraceArtifactVersionRef;
  as_of?: string | null;
  recorded_as_of?: string;
  snapshot_token?: string;
  max_depth?: number;
  max_records?: number;
  max_paths?: number;
  max_path_steps?: number;
}

export interface ProofTraceExportRequest extends ProofTraceRequest {
  as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
  expected_semantic_digest: string;
}

export interface ProofTraceLimits {
  max_depth: number;
  max_records: number;
  max_paths: number;
  max_path_steps: number;
}

export type ProofTraceAssertionKind =
  | "artifact_identity"
  | "artifact_version_identity"
  | "artifact_value"
  | "content_identity"
  | "binding_semantics"
  | "operation_spec_definition"
  | "operation_invocation"
  | "operation_parameters"
  | "operation_environment"
  | "operation_assumptions"
  | "tool_snapshot"
  | "actor_identity"
  | "lifecycle_validity"
  | "physical_semantics_references"
  | "engineering_decision"
  | "decision_evidence"
  | "supersession"
  | "merge_selection"
  | "state_transition"
  | "receipt_attestation";

export type ProofTraceStepRole =
  | "source_invocation"
  | "source_value"
  | "input_binding"
  | "transformation"
  | "output_binding"
  | "intermediate_value"
  | "selected_value";

export type ProofTraceTerminal =
  | { terminal_kind: "source_operation"; reason: "zero_input_operation"; supporting_citation_ids: string[] }
  | { terminal_kind: "source_value"; reason: "producer_absent_after_complete_probe"; supporting_citation_ids: string[] }
  | { terminal_kind: "cycle"; reason: "cycle_detected"; supporting_citation_ids: string[] }
  | { terminal_kind: "ambiguous_producer"; reason: "ambiguous_producer"; supporting_citation_ids: string[] }
  | {
      terminal_kind: "storage_gap";
      reason:
        | "state_history_incomplete"
        | "effective_time_unavailable"
        | "native_receipt_unavailable"
        | "bundle_receipt_unavailable"
        | "semantic_payload_budget_exceeded"
        | "unsupported_semantics";
      supporting_citation_ids: string[];
    }
  | {
      terminal_kind: "truncated";
      reason:
        | "max_depth_reached"
        | "max_records_reached"
        | "max_paths_reached"
        | "max_path_steps_reached"
        | "document_size_limit_reached";
      supporting_citation_ids: string[];
    };

export interface ProofTraceStep {
  ordinal: number;
  step_role: ProofTraceStepRole;
  citation_id: string;
  assertion_ids: string[];
}

export interface ProofTracePath {
  ordinal: number;
  path_id: string;
  steps: ProofTraceStep[];
  terminal: ProofTraceTerminal;
}

interface ProofTraceAssertionBase<K extends ProofTraceAssertionKind, V> {
  assertion_id: string;
  ordinal: number;
  assertion_kind: K;
  subject_citation_id: string;
  value: V;
  supporting_citation_ids: string[];
}

export type ProofTraceChangeCause =
  | "geometry_changed" | "mesh_changed" | "solver_version_changed" | "coordinate_frame_changed"
  | "unit_system_changed" | "schema_changed" | "reference_area_changed" | "parameter_changed"
  | "metadata_only_changed" | "comment_only_changed" | "unknown_impact" | "manual_override";

export type ProofTraceDecisionStatus =
  | "proposed" | "ratified" | "under_review" | "pending_ratification" | "withdrawn" | "superseded";

export type ProofTraceTransitionState =
  | "active" | "fresh" | "stale" | "invalidated" | "superseded" | "pinned" | "deferred"
  | "redacted" | "abandoned" | "inapplicable" | "proposed" | "ratified" | "under_review"
  | "pending_ratification" | "withdrawn";

export interface ProofTraceBindingStateValue {
  state: "unrecorded" | "fresh" | "stale" | "invalidated" | "superseded" | "pinned" | "deferred";
  basis: "explicit_event" | "unrecorded";
  change_cause: ProofTraceChangeCause | null;
  event_citation_id: string | null;
  actor_citation_id: string | null;
  effective_at: string | null;
  recorded_at: string | null;
  reason: string | null;
}

export interface ProofTraceRoleContractValue {
  role: string;
  required: boolean;
  min_count: number;
  max_count: number;
}

export interface ProofTraceEnvironmentValue {
  runtime: string;
  operating_system: string | null;
  architecture: string | null;
  container_digest: string | null;
  dependencies: Record<string, string>;
}

export interface ProofTraceAssumptionValue {
  name: string;
  statement: string;
  reference: string | null;
}

export type ProofTraceAssertion =
  | ProofTraceAssertionBase<"artifact_identity", { logical_id: string; kind: string; name: string; creator_citation_id: string; created_at: string }>
  | ProofTraceAssertionBase<"artifact_version_identity", { artifact_citation_id: string; creator_citation_id: string; created_at: string }>
  | ProofTraceAssertionBase<"artifact_value", { availability: "inline" | "content_identity_only"; structured_content: JsonValue | null }>
  | ProofTraceAssertionBase<"content_identity", {
      algorithm: "sha256"; digest: string; canonicalizer: string | null; canonicalizer_version: string | null;
      byte_length: number | null; media_type: string; source_content_hash: string | null;
    }>
  | ProofTraceAssertionBase<"binding_semantics", {
      operation_citation_id: string; artifact_version_citation_id: string; direction: "input" | "output";
      role: string; ordinal: number; propagates_invalidity: boolean; state: ProofTraceBindingStateValue;
    }>
  | ProofTraceAssertionBase<"operation_spec_definition", {
      name: string; version: number; operation_type: string; parameter_schema: Record<string, JsonValue>;
      input_roles: ProofTraceRoleContractValue[]; output_roles: ProofTraceRoleContractValue[]; definition_hash: string;
    }>
  | ProofTraceAssertionBase<"operation_invocation", {
      operation_spec_citation_id: string; actor_citation_id: string; tool_citation_id: string; status: string;
      started_at: string; completed_at: string; created_at: string; config_hash: string | null; source_ref: string | null;
    }>
  | ProofTraceAssertionBase<"operation_parameters", { parameters: JsonValue }>
  | ProofTraceAssertionBase<"operation_environment", { environment: ProofTraceEnvironmentValue }>
  | ProofTraceAssertionBase<"operation_assumptions", { assumptions: ProofTraceAssumptionValue[] }>
  | ProofTraceAssertionBase<"tool_snapshot", { name: string; version: string; build_id: string | null; source_ref: string | null; definition_hash: string }>
  | ProofTraceAssertionBase<"actor_identity", {
      actor_type: "user" | "service" | "imported" | "unknown"; resolution: "resolved" | "id_only"; name: string | null; role: string | null;
    }>
  | ProofTraceAssertionBase<"lifecycle_validity", {
      lifecycle_state: "active" | "invalidated" | "superseded" | "redacted" | "abandoned" | "inapplicable";
      considered_valid_at_time: boolean; authoritative_for_new_work_at_time: boolean;
      lifecycle_basis: "initial_active_no_lifecycle_event" | "derived_from_exact_lifecycle_timeline" | "independent_state_selector"; as_of: string;
    }>
  | ProofTraceAssertionBase<"physical_semantics_references", { schema_id: string | null; unit_context_id: string | null; coordinate_frame_id: string | null }>
  | ProofTraceAssertionBase<"engineering_decision", {
      decision_key: string; artifact_version_citation_id: string; thread_uri: string; timestamp: string; rationale_spool_uri: string | null;
      status: ProofTraceDecisionStatus; as_of: string; creator_citation_id: string; created_at: string;
    }>
  | ProofTraceAssertionBase<"decision_evidence", {
      decision_citation_id: string;
      artifact_version_citation_id: string;
      effective_at: string;
      recorded_at: string;
    }>
  | ProofTraceAssertionBase<"supersession", {
      old_artifact_version_citation_id: string; new_artifact_version_citation_id: string; change_type: ProofTraceChangeCause;
      reason: string | null; creator_citation_id: string; created_at: string; operation_citation_id: string | null;
    }>
  | ProofTraceAssertionBase<"merge_selection", {
      merge_operation_citation_id: string; source_operation_citation_id: string; included: boolean; rationale: string | null; created_at: string;
    }>
  | ProofTraceAssertionBase<"state_transition", {
      transition_kind: "binding" | "operation" | "artifact_lifecycle" | "decision_status"; target_citation_id: string;
      state: ProofTraceTransitionState;
      event_type: "invalidated" | "superseded" | "redacted" | "abandoned" | "declared_inapplicable" | "reactivated" | null;
      caused_by_artifact_version_citation_id: string | null;
      change_cause: ProofTraceChangeCause | null; actor_citation_id: string;
      effective_at: string | null; recorded_at: string; updated_at: string | null; reason: string | null;
    }>
  | ProofTraceAssertionBase<"receipt_attestation", {
      attestation_kind: "operation_registration" | "artifact_invalidation"; request_hash: string; audit_event_id: string;
      domain_event_id: string; recorded_at: string;
    }>;

export type ProofTraceRecordKind =
  | "artifact"
  | "artifact_version"
  | "operation_spec"
  | "operation_run"
  | "tool"
  | "actor"
  | "binding"
  | "binding_state_event"
  | "operation_state_event"
  | "supersession"
  | "merge_input"
  | "lifecycle_event"
  | "decision"
  | "decision_status_event";

export interface ProofTraceRecordCitation {
  citation_kind: "record";
  citation_id: string;
  record_kind: ProofTraceRecordKind;
  id: string;
  temporal_basis: "recorded_fact" | "derived_from_owning_operation" | "current_authorization_overlay";
  effective_at: string | null;
  recorded_at: string | null;
}

export interface ProofTraceDecisionEvidenceCitation {
  citation_kind: "decision_evidence";
  citation_id: string;
  decision_id: string;
  ordinal: number;
  artifact_version_id: string;
  effective_at: string;
  recorded_at: string;
}

interface ProofTraceReceiptCitationBase {
  citation_id: string;
  project_id: string;
  idempotency_key: string;
  request_hash: string;
  audit_event_id: string;
  domain_event_id: string;
  recorded_at: string;
}

export interface ProofTraceOperationReceiptCitation extends ProofTraceReceiptCitationBase {
  citation_kind: "operation_registration_receipt";
  operation_id: string;
}

export interface ProofTraceInvalidationReceiptCitation extends ProofTraceReceiptCitationBase {
  citation_kind: "artifact_invalidation_receipt";
  artifact_version_id: string;
  lifecycle_event_id: string;
}

export type ProofTraceCitation =
  | ProofTraceRecordCitation
  | ProofTraceDecisionEvidenceCitation
  | ProofTraceOperationReceiptCitation
  | ProofTraceInvalidationReceiptCitation;

export type ProofTraceGapCode =
  | "max_depth_reached"
  | "max_records_reached"
  | "max_paths_reached"
  | "max_path_steps_reached"
  | "semantic_payload_budget_exceeded"
  | "document_size_limit_reached"
  | "cycle_detected"
  | "ambiguous_producer"
  | "state_history_incomplete"
  | "effective_time_unavailable"
  | "native_receipt_unavailable"
  | "bundle_receipt_unavailable"
  | "actor_directory_unavailable"
  | "journal_entries_unavailable"
  | "physical_schema_definition_unavailable"
  | "unit_context_definition_unavailable"
  | "coordinate_frame_definition_unavailable"
  | "reference_quantities_unavailable"
  | "unknown_record_type"
  | "unknown_relationship_type"
  | "unsupported_semantics";

export interface ProofTraceGap {
  code: ProofTraceGapCode;
  subject_citation_id: string | null;
}

export interface ProofTraceCompleteness {
  traversal: "complete" | "incomplete";
  evidence: "complete" | "incomplete";
  temporal: "effective_and_recorded_time";
  gaps: ProofTraceGap[];
  redaction_terminal: {
    terminal_kind: "redacted";
    reason: "restricted_evidence_omitted";
  } | null;
}

export interface ProofTraceDocument {
  contract_version: typeof PROOF_TRACE_CONTRACT_VERSION;
  snapshot: {
    project_id: string;
    root_ref: ProofTraceArtifactVersionRef;
    effective_as_of: string;
    recorded_as_of: string;
    snapshot_token: string;
    temporal_basis: "effective_and_recorded_time";
    source_contract_version: "thread.lineage.v2";
    limits: ProofTraceLimits;
  };
  paths: ProofTracePath[];
  assertions: ProofTraceAssertion[];
  citations: ProofTraceCitation[];
  completeness: ProofTraceCompleteness;
}

export interface ProofTraceSemanticIdentity {
  digest: string;
  canonicalizer: "rfc8785";
  canonicalizer_version: "1";
  byte_length: number;
  media_type: "application/vnd.thread.proof-trace.v2+json";
}

export interface ProofTraceResult {
  document: ProofTraceDocument;
  semantic_identity: ProofTraceSemanticIdentity;
}

export interface ProofTraceEnvelope {
  schema_version: "thread.api.v1";
  request_id: string;
  data: ProofTraceResult;
  page: null;
  provenance: {
    source: "thread_core";
    projection_version: typeof PROOF_TRACE_CONTRACT_VERSION;
    schema_migration: string;
  };
  warnings: string[];
}

export interface ProofTraceExport {
  blob: Blob;
  filename: string;
  mediaType: string;
  semanticDigest: string;
}

export type ProofTraceExportFormat = "json" | "html";

export class ProofTraceValidationError extends Error {
  readonly path: string;

  constructor(path: string, expectation: string) {
    super(`${path}: ${expectation}`);
    this.name = "ProofTraceValidationError";
    this.path = path;
  }
}

export class ProofTraceChangedError extends Error {
  constructor() {
    super("The proof changed after it was displayed. Refresh the proof before exporting it.");
    this.name = "ProofTraceChangedError";
  }
}

type UnknownRecord = Record<string, unknown>;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FIXED_UTC_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/;
const AWARE_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const AWARE_TIMESTAMP_PARTS = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|([+-])(\d{2}):(\d{2}))$/;
const SHA256_PATTERN = /^sha256:[0-9a-f]{64}$/;
const SNAPSHOT_TOKEN_PATTERN = /^ts1\.[0-9a-f]{16}\.[A-Za-z0-9_-]{39,4060}$/;
const HEX_SHA256_PATTERN = /^[0-9a-f]{64}$/;
const SOURCE_HASH_PATTERN = /^[a-z0-9_-]+:[0-9a-f]+$/;
const IDEMPOTENCY_BOUNDARY_WHITESPACE_PATTERN = /^[\u0009-\u000d\u001c-\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]|[\u0009-\u000d\u001c-\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]$/u;

const ASSERTION_KINDS: readonly ProofTraceAssertionKind[] = [
  "artifact_identity", "artifact_version_identity", "artifact_value", "content_identity", "binding_semantics",
  "operation_spec_definition", "operation_invocation", "operation_parameters", "operation_environment",
  "operation_assumptions", "tool_snapshot", "actor_identity", "lifecycle_validity",
  "physical_semantics_references", "engineering_decision", "decision_evidence", "supersession",
  "merge_selection", "state_transition", "receipt_attestation",
];
const STEP_ROLES: readonly ProofTraceStepRole[] = [
  "source_invocation", "source_value", "input_binding", "transformation", "output_binding",
  "intermediate_value", "selected_value",
];
const RECORD_KINDS: readonly ProofTraceRecordKind[] = [
  "artifact", "artifact_version", "operation_spec", "operation_run", "tool", "actor", "binding",
  "binding_state_event", "operation_state_event", "supersession", "merge_input", "lifecycle_event", "decision",
  "decision_status_event",
];
const CHANGE_CAUSES: readonly ProofTraceChangeCause[] = [
  "geometry_changed", "mesh_changed", "solver_version_changed", "coordinate_frame_changed", "unit_system_changed",
  "schema_changed", "reference_area_changed", "parameter_changed", "metadata_only_changed", "comment_only_changed",
  "unknown_impact", "manual_override",
];
const DECISION_STATUSES: readonly ProofTraceDecisionStatus[] = [
  "proposed", "ratified", "under_review", "pending_ratification", "withdrawn", "superseded",
];
const TRANSITION_STATES: readonly ProofTraceTransitionState[] = [
  "active", "fresh", "stale", "invalidated", "superseded", "pinned", "deferred", "redacted", "abandoned",
  "inapplicable", "proposed", "ratified", "under_review", "pending_ratification", "withdrawn",
];
const GAP_CODES: readonly ProofTraceGapCode[] = [
  "max_depth_reached", "max_records_reached", "max_paths_reached", "max_path_steps_reached",
  "semantic_payload_budget_exceeded", "document_size_limit_reached", "cycle_detected", "ambiguous_producer",
  "state_history_incomplete", "effective_time_unavailable", "native_receipt_unavailable", "bundle_receipt_unavailable",
  "actor_directory_unavailable", "journal_entries_unavailable", "physical_schema_definition_unavailable", "unit_context_definition_unavailable",
  "coordinate_frame_definition_unavailable", "reference_quantities_unavailable", "unknown_record_type",
  "unknown_relationship_type", "unsupported_semantics",
];

function fail(path: string, expectation: string): never {
  throw new ProofTraceValidationError(path, expectation);
}

function record(value: unknown, path: string): UnknownRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) fail(path, "expected an object");
  return value as UnknownRecord;
}

function exactRecord(value: unknown, path: string, keys: readonly string[], optional: readonly string[] = []): UnknownRecord {
  const item = record(value, path);
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(item, key)) fail(`${path}.${key}`, "missing required field");
  }
  const allowed = new Set([...keys, ...optional]);
  for (const key of Object.keys(item)) {
    if (!allowed.has(key)) fail(`${path}.${key}`, "unknown field on a known contract object");
  }
  return item;
}

function array(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) fail(path, "expected an array");
  return value;
}

function boundedArray(value: unknown, path: string, max: number, min = 0): unknown[] {
  const result = array(value, path);
  if (result.length < min || result.length > max) fail(path, `expected between ${min} and ${max} items`);
  return result;
}

function text(value: unknown, path: string, allowEmpty = false): string {
  if (typeof value !== "string" || (!allowEmpty && value.length === 0)) fail(path, "expected a non-empty string");
  return value;
}

function boundedText(value: unknown, path: string, max: number, allowEmpty = false): string {
  const result = text(value, path, allowEmpty);
  if ([...result].length > max) fail(path, `expected at most ${max} characters`);
  return result;
}

function nullableText(value: unknown, path: string, max: number): string | null {
  return value === null ? null : boundedText(value, path, max, true);
}

function uuid(value: unknown, path: string): string {
  const result = text(value, path);
  if (!UUID_PATTERN.test(result)) fail(path, "expected a UUID");
  return result;
}

function canonicalUuid(value: unknown, path: string): string {
  const result = uuid(value, path);
  if (result !== result.toLowerCase()) fail(path, "expected a canonical lowercase UUID");
  return result;
}

function nullableUuid(value: unknown, path: string): string | null {
  return value === null ? null : canonicalUuid(value, path);
}

function literal<T extends string>(value: unknown, path: string, choices: readonly T[]): T {
  if (typeof value !== "string" || !choices.includes(value as T)) fail(path, `expected one of: ${choices.join(", ")}`);
  return value as T;
}

function integer(value: unknown, path: string, min = 0, max = Number.MAX_SAFE_INTEGER): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
    fail(path, `expected an integer between ${min} and ${max}`);
  }
  return value;
}

function boolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") fail(path, "expected a boolean");
  return value;
}

function validAwareTimestampComponents(value: string): boolean {
  const match = AWARE_TIMESTAMP_PARTS.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === "Z" ? 0 : Number(match[10]);
  const offsetMinute = match[8] === "Z" ? 0 : Number(match[11]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const monthLengths = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const normalizedMillis = Date.parse(value);
  const normalizedUtcYear = Number.isFinite(normalizedMillis) ? new Date(normalizedMillis).getUTCFullYear() : 0;
  return year >= 1
    && month >= 1 && month <= 12
    && day >= 1 && day <= monthLengths[month - 1]!
    && hour <= 23 && minute <= 59 && second <= 59
    && offsetHour <= 23 && offsetMinute <= 59
    && normalizedUtcYear >= 1 && normalizedUtcYear <= 9999;
}

function fixedUtcTimestamp(value: unknown, path: string): string {
  const result = text(value, path);
  if (!FIXED_UTC_TIMESTAMP_PATTERN.test(result) || !validAwareTimestampComponents(result)) {
    fail(path, "expected a UTC timestamp with exactly six fractional digits");
  }
  return result;
}

function nullableFixedUtcTimestamp(value: unknown, path: string): string | null {
  return value === null ? null : fixedUtcTimestamp(value, path);
}

function awareTimestamp(value: unknown, path: string): string {
  const result = text(value, path);
  const requestYear = Number(result.slice(0, 4));
  const yearAllowed = (requestYear >= 2 && requestYear <= 9998)
    || ((requestYear === 1 || requestYear === 9999) && result.endsWith("Z"));
  if (!AWARE_TIMESTAMP_PATTERN.test(result) || !validAwareTimestampComponents(result) || !yearAllowed) {
    fail(path, "expected an ISO-8601 timestamp with a timezone offset");
  }
  return result;
}

function snapshotToken(value: unknown, path: string): string {
  const result = text(value, path);
  if (!SNAPSHOT_TOKEN_PATTERN.test(result)) fail(path, "expected a canonical keyring-bound ts1 snapshot token");
  return result;
}

function digest(value: unknown, path: string): string {
  const result = text(value, path);
  if (!SHA256_PATTERN.test(result)) fail(path, "expected a lowercase SHA-256 digest");
  return result;
}

function hexDigest(value: unknown, path: string): string {
  const result = text(value, path);
  if (!HEX_SHA256_PATTERN.test(result)) fail(path, "expected 64 lowercase hexadecimal characters");
  return result;
}

function sortedUniqueDigests(value: unknown, path: string, min = 0, max = Number.MAX_SAFE_INTEGER): string[] {
  const result = array(value, path).map((item, index) => digest(item, `${path}[${index}]`));
  if (result.length < min || result.length > max) fail(path, `expected between ${min} and ${max} values`);
  if (new Set(result).size !== result.length || result.some((item, index) => index > 0 && result[index - 1]! > item)) {
    fail(path, "expected unique values in lexical order");
  }
  return result;
}

function validUnicodeScalarString(value: string, path: string): void {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) fail(path, "JCS cannot encode an unpaired Unicode surrogate");
      index += 1;
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      fail(path, "JCS cannot encode an unpaired Unicode surrogate");
    }
  }
}

interface JsonValueMetrics {
  nodes: number;
  maximumDepth: number;
  canonicalBytes: number;
}

interface SemanticPayloadBudget {
  nodes: number;
  canonicalBytes: number;
}

interface JsonValueFrame {
  value: unknown;
  path: string;
  depth: number;
}

function canonicalStringByteLength(value: string, path: string): number {
  let bytes = 2;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code === 0x22 || code === 0x5c || code === 0x08 || code === 0x09 || code === 0x0a || code === 0x0c || code === 0x0d) {
      bytes += 2;
    } else if (code <= 0x1f) {
      bytes += 6;
    } else if (code <= 0x7f) {
      bytes += 1;
    } else if (code <= 0x7ff) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) fail(path, "JCS cannot encode an unpaired Unicode surrogate");
      bytes += 4;
      index += 1;
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      fail(path, "JCS cannot encode an unpaired Unicode surrogate");
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

function validateJsonValueStructure(value: unknown, path: string): { nodes: number; maximumDepth: number } {
  // Contract accounting starts the root at depth one. Every JSON value,
  // container, and object member name is a node; member names do not add depth.
  const stack: JsonValueFrame[] = [{ value, path, depth: 1 }];
  let nodes = 0;
  let maximumDepth = 0;
  while (stack.length) {
    const frame = stack.pop()!;
    nodes += 1;
    maximumDepth = Math.max(maximumDepth, frame.depth);
    if (maximumDepth > PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_depth) {
      fail(frame.path, `semantic JSON exceeds maximum depth ${PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_depth}`);
    }
    if (nodes > PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_nodes) {
      fail(path, `semantic JSON exceeds maximum node count ${PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_nodes}`);
    }
    const entry = frame.value;
    if (entry === null || typeof entry === "boolean") continue;
    if (typeof entry === "string") {
      validUnicodeScalarString(entry, frame.path);
      continue;
    }
    if (typeof entry === "number") {
      if (!Number.isFinite(entry) || (Number.isInteger(entry) && !Number.isSafeInteger(entry))) {
        fail(frame.path, "expected a finite JSON number in the RFC 8785 safe-integer domain");
      }
      continue;
    }
    if (Array.isArray(entry)) {
      if (nodes + stack.length + entry.length > PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_nodes) {
        fail(path, `semantic JSON exceeds maximum node count ${PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_nodes}`);
      }
      for (let index = entry.length - 1; index >= 0; index -= 1) {
        stack.push({ value: entry[index], path: `${frame.path}[${index}]`, depth: frame.depth + 1 });
      }
      continue;
    }
    if (typeof entry === "object") {
      const keys: string[] = [];
      for (const key in entry as UnknownRecord) {
        if (!Object.prototype.hasOwnProperty.call(entry, key)) continue;
        if (nodes + stack.length + (keys.length + 1) * 2 > PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_nodes) {
          fail(path, `semantic JSON exceeds maximum node count ${PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_nodes}`);
        }
        keys.push(key);
      }
      nodes += keys.length;
      for (let index = keys.length - 1; index >= 0; index -= 1) {
        const key = keys[index]!;
        validUnicodeScalarString(key, `${frame.path}.<key>`);
        stack.push({ value: (entry as UnknownRecord)[key], path: `${frame.path}.${key}`, depth: frame.depth + 1 });
      }
      continue;
    }
    fail(frame.path, "expected a JSON value");
  }
  return { nodes, maximumDepth };
}

function canonicalJsonByteLength(value: unknown, path: string, maximumBytes: number): number {
  const stack: Array<{ value: unknown; path: string }> = [{ value, path }];
  let bytes = 0;
  const add = (count: number) => {
    bytes += count;
    if (bytes > maximumBytes) fail(path, `canonical JSON exceeds maximum byte length ${maximumBytes}`);
  };
  while (stack.length) {
    const frame = stack.pop()!;
    const entry = frame.value;
    if (entry === null) {
      add(4);
    } else if (typeof entry === "boolean") {
      add(entry ? 4 : 5);
    } else if (typeof entry === "number") {
      const encoded = JSON.stringify(entry);
      if (encoded === undefined) fail(frame.path, "JCS requires a finite number in the safe-integer domain");
      add(encoded.length);
    } else if (typeof entry === "string") {
      add(canonicalStringByteLength(entry, frame.path));
    } else if (Array.isArray(entry)) {
      add(2 + Math.max(0, entry.length - 1));
      for (let index = entry.length - 1; index >= 0; index -= 1) {
        stack.push({ value: entry[index], path: `${frame.path}[${index}]` });
      }
    } else if (typeof entry === "object") {
      const keys = Object.keys(entry);
      add(2 + Math.max(0, keys.length - 1) + keys.length);
      for (let index = keys.length - 1; index >= 0; index -= 1) {
        const key = keys[index]!;
        add(canonicalStringByteLength(key, `${frame.path}.<key>`));
        stack.push({ value: (entry as UnknownRecord)[key], path: `${frame.path}.${key}` });
      }
    } else {
      fail(frame.path, "expected a JSON value");
    }
  }
  return bytes;
}

function semanticJsonValue(value: unknown, path: string, budget: SemanticPayloadBudget): JsonValueMetrics {
  const structure = validateJsonValueStructure(value, path);
  const canonicalBytes = canonicalJsonByteLength(
    value,
    path,
    PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_canonical_bytes,
  );
  budget.nodes += structure.nodes;
  budget.canonicalBytes += canonicalBytes;
  if (budget.nodes > PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_aggregate_nodes) {
    fail(path, `proof semantic payloads exceed aggregate node count ${PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_aggregate_nodes}`);
  }
  if (budget.canonicalBytes > PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_aggregate_canonical_bytes) {
    fail(path, `proof semantic payloads exceed aggregate canonical bytes ${PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_aggregate_canonical_bytes}`);
  }
  return { ...structure, canonicalBytes };
}

type CanonicalFrame =
  | { kind: "value"; value: JsonValue; path: string }
  | { kind: "token"; value: string };

function canonicalJson(value: JsonValue, path = "$canonical"): string {
  const chunks: string[] = [];
  const stack: CanonicalFrame[] = [{ kind: "value", value, path }];
  while (stack.length) {
    const frame = stack.pop()!;
    if (frame.kind === "token") {
      chunks.push(frame.value);
      continue;
    }
    const entry = frame.value;
    if (entry === null || typeof entry === "boolean") {
      chunks.push(String(entry));
    } else if (typeof entry === "number") {
      if (!Number.isFinite(entry) || (Number.isInteger(entry) && !Number.isSafeInteger(entry))) {
        fail(frame.path, "JCS requires a finite number in the safe-integer domain");
      }
      chunks.push(JSON.stringify(entry));
    } else if (typeof entry === "string") {
      validUnicodeScalarString(entry, frame.path);
      chunks.push(JSON.stringify(entry));
    } else if (Array.isArray(entry)) {
      chunks.push("[");
      stack.push({ kind: "token", value: "]" });
      for (let index = entry.length - 1; index >= 0; index -= 1) {
        stack.push({ kind: "value", value: entry[index]!, path: `${frame.path}[${index}]` });
        if (index > 0) stack.push({ kind: "token", value: "," });
      }
    } else {
      const keys = Object.keys(entry).sort();
      chunks.push("{");
      stack.push({ kind: "token", value: "}" });
      for (let index = keys.length - 1; index >= 0; index -= 1) {
        const key = keys[index]!;
        validUnicodeScalarString(key, `${frame.path}.<key>`);
        stack.push({ kind: "value", value: entry[key]!, path: `${frame.path}.${key}` });
        stack.push({ kind: "token", value: ":" });
        stack.push({ kind: "token", value: JSON.stringify(key) });
        if (index > 0) stack.push({ kind: "token", value: "," });
      }
    }
  }
  return chunks.join("");
}

function canonicalBytes(value: JsonValue): Uint8Array {
  return new TextEncoder().encode(canonicalJson(value));
}

async function sha256Identity(value: JsonValue): Promise<string> {
  return sha256Bytes(canonicalBytes(value));
}

async function sha256Bytes(value: Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) fail("$crypto", "WebCrypto SHA-256 is required to verify proof identities");
  const result = await globalThis.crypto.subtle.digest("SHA-256", value);
  return `sha256:${[...new Uint8Array(result)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return;
  if (typeof signal.throwIfAborted === "function") signal.throwIfAborted();
  throw new DOMException("The proof verification was aborted.", "AbortError");
}

interface ProofIdentityCheck {
  path: string;
  expected: string;
  payload: JsonValue;
}

async function verifyIdentityChecks(checks: ProofIdentityCheck[], signal?: AbortSignal): Promise<void> {
  let next = 0;
  const worker = async () => {
    while (next < checks.length) {
      throwIfAborted(signal);
      const check = checks[next++]!;
      const actual = await sha256Identity(check.payload);
      if (actual !== check.expected) fail(check.path, "does not identify its exact RFC 8785 semantic payload");
    }
  };
  await Promise.all(Array.from({ length: Math.min(8, checks.length) }, () => worker()));
  throwIfAborted(signal);
}

function nullableChangeCause(value: unknown, path: string): ProofTraceChangeCause | null {
  return value === null ? null : literal(value, path, CHANGE_CAUSES);
}

function validateRoleContract(value: unknown, path: string): ProofTraceRoleContractValue {
  const item = exactRecord(value, path, ["role", "required", "min_count", "max_count"]);
  boundedText(item.role, `${path}.role`, 100);
  const required = boolean(item.required, `${path}.required`);
  const min = integer(item.min_count, `${path}.min_count`, 0, 1000);
  const max = integer(item.max_count, `${path}.max_count`, 1, 1000);
  if (required && min < 1) fail(`${path}.min_count`, "required roles need at least one value");
  if (min > max) fail(path, "min_count cannot exceed max_count");
  return value as ProofTraceRoleContractValue;
}

function validateBindingState(value: unknown, path: string): ProofTraceBindingStateValue {
  const item = exactRecord(value, path, [
    "state", "basis", "change_cause", "event_citation_id", "actor_citation_id", "effective_at", "recorded_at", "reason",
  ]);
  const state = literal(item.state, `${path}.state`, ["unrecorded", "fresh", "stale", "invalidated", "superseded", "pinned", "deferred"] as const);
  const basis = literal(item.basis, `${path}.basis`, ["explicit_event", "unrecorded"] as const);
  const cause = nullableChangeCause(item.change_cause, `${path}.change_cause`);
  const eventCitation = item.event_citation_id === null ? null : digest(item.event_citation_id, `${path}.event_citation_id`);
  const actorCitation = item.actor_citation_id === null ? null : digest(item.actor_citation_id, `${path}.actor_citation_id`);
  const effectiveAt = nullableFixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  const recordedAt = nullableFixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  const reason = item.reason === null ? null : nullableText(item.reason, `${path}.reason`, 2000);
  if (basis === "unrecorded") {
    if (state !== "unrecorded" || [cause, eventCitation, actorCitation, effectiveAt, recordedAt, reason].some((entry) => entry !== null)) {
      fail(path, "unrecorded state cannot carry an event payload");
    }
  } else if (state === "unrecorded" || cause === null || eventCitation === null || actorCitation === null || recordedAt === null) {
    fail(path, "explicit state requires state, cause, event, actor and recorded_at");
  }
  return value as ProofTraceBindingStateValue;
}

function validateEnvironment(value: unknown, path: string, semanticBudget: SemanticPayloadBudget): ProofTraceEnvironmentValue {
  const item = exactRecord(value, path, ["runtime", "operating_system", "architecture", "container_digest", "dependencies"]);
  semanticJsonValue(value, path, semanticBudget);
  boundedText(item.runtime, `${path}.runtime`, 300);
  nullableText(item.operating_system, `${path}.operating_system`, 300);
  nullableText(item.architecture, `${path}.architecture`, 100);
  nullableText(item.container_digest, `${path}.container_digest`, 500);
  const dependencies = record(item.dependencies, `${path}.dependencies`);
  Object.entries(dependencies).forEach(([key, dependency]) => text(dependency, `${path}.dependencies.${key}`, true));
  return value as ProofTraceEnvironmentValue;
}

function validateAssertionValue(
  kind: ProofTraceAssertionKind,
  value: unknown,
  path: string,
  semanticBudget: SemanticPayloadBudget,
): JsonValue {
  switch (kind) {
    case "artifact_identity": {
      const item = exactRecord(value, path, ["logical_id", "kind", "name", "creator_citation_id", "created_at"]);
      canonicalUuid(item.logical_id, `${path}.logical_id`);
      boundedText(item.kind, `${path}.kind`, 100);
      boundedText(item.name, `${path}.name`, 300);
      digest(item.creator_citation_id, `${path}.creator_citation_id`);
      fixedUtcTimestamp(item.created_at, `${path}.created_at`);
      break;
    }
    case "artifact_version_identity": {
      const item = exactRecord(value, path, ["artifact_citation_id", "creator_citation_id", "created_at"]);
      digest(item.artifact_citation_id, `${path}.artifact_citation_id`);
      digest(item.creator_citation_id, `${path}.creator_citation_id`);
      fixedUtcTimestamp(item.created_at, `${path}.created_at`);
      break;
    }
    case "artifact_value": {
      const item = exactRecord(value, path, ["availability", "structured_content"]);
      const availability = literal(item.availability, `${path}.availability`, ["inline", "content_identity_only"] as const);
      if ((availability === "inline") !== (item.structured_content !== null)) fail(path, "availability must match structured_content presence");
      if (item.structured_content !== null) {
        semanticJsonValue(item.structured_content, `${path}.structured_content`, semanticBudget);
      }
      break;
    }
    case "content_identity": {
      const item = exactRecord(value, path, [
        "algorithm", "digest", "canonicalizer", "canonicalizer_version", "byte_length", "media_type", "source_content_hash",
      ]);
      literal(item.algorithm, `${path}.algorithm`, ["sha256"]);
      hexDigest(item.digest, `${path}.digest`);
      const canonicalizer = item.canonicalizer === null ? null : text(item.canonicalizer, `${path}.canonicalizer`, true);
      const canonicalizerVersion = item.canonicalizer_version === null ? null : text(item.canonicalizer_version, `${path}.canonicalizer_version`, true);
      if ((canonicalizer === null) !== (canonicalizerVersion === null)) fail(path, "canonicalizer and version must both be set or null");
      if (item.byte_length !== null) integer(item.byte_length, `${path}.byte_length`);
      boundedText(item.media_type, `${path}.media_type`, 255);
      if (item.source_content_hash !== null) {
        const sourceHash = text(item.source_content_hash, `${path}.source_content_hash`);
        if (!SOURCE_HASH_PATTERN.test(sourceHash)) fail(`${path}.source_content_hash`, "expected algorithm:lowercase-hex");
      }
      break;
    }
    case "binding_semantics": {
      const item = exactRecord(value, path, [
        "operation_citation_id", "artifact_version_citation_id", "direction", "role", "ordinal", "propagates_invalidity", "state",
      ]);
      digest(item.operation_citation_id, `${path}.operation_citation_id`);
      digest(item.artifact_version_citation_id, `${path}.artifact_version_citation_id`);
      const direction = literal(item.direction, `${path}.direction`, ["input", "output"] as const);
      boundedText(item.role, `${path}.role`, 100);
      integer(item.ordinal, `${path}.ordinal`);
      const propagates = boolean(item.propagates_invalidity, `${path}.propagates_invalidity`);
      if (direction === "output" && propagates) fail(`${path}.propagates_invalidity`, "output bindings cannot propagate invalidity");
      validateBindingState(item.state, `${path}.state`);
      break;
    }
    case "operation_spec_definition": {
      const item = exactRecord(value, path, ["name", "version", "operation_type", "parameter_schema", "input_roles", "output_roles", "definition_hash"]);
      boundedText(item.name, `${path}.name`, 128);
      integer(item.version, `${path}.version`, 1);
      boundedText(item.operation_type, `${path}.operation_type`, 100);
      record(item.parameter_schema, `${path}.parameter_schema`);
      semanticJsonValue(item.parameter_schema, `${path}.parameter_schema`, semanticBudget);
      const inputRoles = array(item.input_roles, `${path}.input_roles`);
      const outputRoles = array(item.output_roles, `${path}.output_roles`);
      if (inputRoles.length > 100 || !outputRoles.length || outputRoles.length > 100) fail(path, "role counts are outside contract bounds");
      inputRoles.forEach((role, index) => validateRoleContract(role, `${path}.input_roles[${index}]`));
      outputRoles.forEach((role, index) => validateRoleContract(role, `${path}.output_roles[${index}]`));
      for (const [direction, roles] of [["input", inputRoles], ["output", outputRoles]] as const) {
        const roleNames = roles.map((role) => (role as ProofTraceRoleContractValue).role);
        if (new Set(roleNames).size !== roleNames.length) {
          fail(`${path}.${direction}_roles`, `${direction} role contracts must be unique`);
        }
      }
      digest(item.definition_hash, `${path}.definition_hash`);
      break;
    }
    case "operation_invocation": {
      const item = exactRecord(value, path, [
        "operation_spec_citation_id", "actor_citation_id", "tool_citation_id", "status", "started_at", "completed_at", "created_at", "config_hash", "source_ref",
      ]);
      ["operation_spec_citation_id", "actor_citation_id", "tool_citation_id"].forEach((key) => digest(item[key], `${path}.${key}`));
      boundedText(item.status, `${path}.status`, 100);
      const startedAt = fixedUtcTimestamp(item.started_at, `${path}.started_at`);
      const completedAt = fixedUtcTimestamp(item.completed_at, `${path}.completed_at`);
      fixedUtcTimestamp(item.created_at, `${path}.created_at`);
      if (completedAt < startedAt) fail(`${path}.completed_at`, "cannot precede started_at");
      nullableText(item.config_hash, `${path}.config_hash`, 500);
      nullableText(item.source_ref, `${path}.source_ref`, 2000);
      break;
    }
    case "operation_parameters": {
      const item = exactRecord(value, path, ["parameters"]);
      semanticJsonValue(item.parameters, `${path}.parameters`, semanticBudget);
      break;
    }
    case "operation_environment": {
      const item = exactRecord(value, path, ["environment"]);
      validateEnvironment(item.environment, `${path}.environment`, semanticBudget);
      break;
    }
    case "operation_assumptions": {
      const item = exactRecord(value, path, ["assumptions"]);
      const assumptions = array(item.assumptions, `${path}.assumptions`);
      if (assumptions.length > 100) fail(`${path}.assumptions`, "expected at most 100 assumptions");
      assumptions.forEach((assumption, index) => {
        const assumptionPath = `${path}.assumptions[${index}]`;
        const entry = exactRecord(assumption, assumptionPath, ["name", "statement", "reference"]);
        boundedText(entry.name, `${assumptionPath}.name`, 300);
        boundedText(entry.statement, `${assumptionPath}.statement`, 4000);
        nullableText(entry.reference, `${assumptionPath}.reference`, 2000);
      });
      break;
    }
    case "tool_snapshot": {
      const item = exactRecord(value, path, ["name", "version", "build_id", "source_ref", "definition_hash"]);
      boundedText(item.name, `${path}.name`, 200);
      boundedText(item.version, `${path}.version`, 200);
      nullableText(item.build_id, `${path}.build_id`, 500);
      nullableText(item.source_ref, `${path}.source_ref`, 2000);
      digest(item.definition_hash, `${path}.definition_hash`);
      break;
    }
    case "actor_identity": {
      const item = exactRecord(value, path, ["actor_type", "resolution", "name", "role"]);
      literal(item.actor_type, `${path}.actor_type`, ["user", "service", "imported", "unknown"] as const);
      const resolution = literal(item.resolution, `${path}.resolution`, ["resolved", "id_only"] as const);
      const name = nullableText(item.name, `${path}.name`, 300);
      const role = nullableText(item.role, `${path}.role`, 300);
      if (resolution === "id_only" && (name !== null || role !== null)) fail(path, "id-only actor cannot disclose name or role");
      break;
    }
    case "lifecycle_validity": {
      const item = exactRecord(value, path, [
        "lifecycle_state", "considered_valid_at_time", "authoritative_for_new_work_at_time", "lifecycle_basis", "as_of",
      ]);
      literal(item.lifecycle_state, `${path}.lifecycle_state`, ["active", "invalidated", "superseded", "redacted", "abandoned", "inapplicable"] as const);
      boolean(item.considered_valid_at_time, `${path}.considered_valid_at_time`);
      boolean(item.authoritative_for_new_work_at_time, `${path}.authoritative_for_new_work_at_time`);
      literal(item.lifecycle_basis, `${path}.lifecycle_basis`, ["initial_active_no_lifecycle_event", "derived_from_exact_lifecycle_timeline", "independent_state_selector"] as const);
      fixedUtcTimestamp(item.as_of, `${path}.as_of`);
      break;
    }
    case "physical_semantics_references": {
      const item = exactRecord(value, path, ["schema_id", "unit_context_id", "coordinate_frame_id"]);
      nullableUuid(item.schema_id, `${path}.schema_id`);
      nullableUuid(item.unit_context_id, `${path}.unit_context_id`);
      nullableUuid(item.coordinate_frame_id, `${path}.coordinate_frame_id`);
      break;
    }
    case "engineering_decision": {
      const item = exactRecord(value, path, [
        "decision_key", "artifact_version_citation_id", "thread_uri", "timestamp", "rationale_spool_uri", "status", "as_of", "creator_citation_id", "created_at",
      ]);
      boundedText(item.decision_key, `${path}.decision_key`, 200);
      digest(item.artifact_version_citation_id, `${path}.artifact_version_citation_id`);
      boundedText(item.thread_uri, `${path}.thread_uri`, 2000);
      fixedUtcTimestamp(item.timestamp, `${path}.timestamp`);
      nullableText(item.rationale_spool_uri, `${path}.rationale_spool_uri`, 2000);
      literal(item.status, `${path}.status`, DECISION_STATUSES);
      fixedUtcTimestamp(item.as_of, `${path}.as_of`);
      digest(item.creator_citation_id, `${path}.creator_citation_id`);
      fixedUtcTimestamp(item.created_at, `${path}.created_at`);
      break;
    }
    case "decision_evidence": {
      const item = exactRecord(value, path, [
        "decision_citation_id", "artifact_version_citation_id", "effective_at", "recorded_at",
      ]);
      digest(item.decision_citation_id, `${path}.decision_citation_id`);
      digest(item.artifact_version_citation_id, `${path}.artifact_version_citation_id`);
      fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
      fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
      break;
    }
    case "supersession": {
      const item = exactRecord(value, path, [
        "old_artifact_version_citation_id", "new_artifact_version_citation_id", "change_type", "reason", "creator_citation_id", "created_at", "operation_citation_id",
      ]);
      digest(item.old_artifact_version_citation_id, `${path}.old_artifact_version_citation_id`);
      digest(item.new_artifact_version_citation_id, `${path}.new_artifact_version_citation_id`);
      literal(item.change_type, `${path}.change_type`, CHANGE_CAUSES);
      nullableText(item.reason, `${path}.reason`, 2000);
      digest(item.creator_citation_id, `${path}.creator_citation_id`);
      fixedUtcTimestamp(item.created_at, `${path}.created_at`);
      if (item.operation_citation_id !== null) digest(item.operation_citation_id, `${path}.operation_citation_id`);
      break;
    }
    case "merge_selection": {
      const item = exactRecord(value, path, ["merge_operation_citation_id", "source_operation_citation_id", "included", "rationale", "created_at"]);
      digest(item.merge_operation_citation_id, `${path}.merge_operation_citation_id`);
      digest(item.source_operation_citation_id, `${path}.source_operation_citation_id`);
      boolean(item.included, `${path}.included`);
      nullableText(item.rationale, `${path}.rationale`, 2000);
      fixedUtcTimestamp(item.created_at, `${path}.created_at`);
      break;
    }
    case "state_transition": {
      const item = exactRecord(value, path, [
        "transition_kind", "target_citation_id", "state", "event_type", "caused_by_artifact_version_citation_id", "change_cause", "actor_citation_id", "effective_at", "recorded_at", "updated_at", "reason",
      ]);
      const transitionKind = literal(item.transition_kind, `${path}.transition_kind`, ["binding", "operation", "artifact_lifecycle", "decision_status"] as const);
      digest(item.target_citation_id, `${path}.target_citation_id`);
      const state = literal(item.state, `${path}.state`, TRANSITION_STATES);
      const eventType = item.event_type === null ? null : literal(item.event_type, `${path}.event_type`, ["invalidated", "superseded", "redacted", "abandoned", "declared_inapplicable", "reactivated"] as const);
      const causedBy = item.caused_by_artifact_version_citation_id === null ? null : digest(item.caused_by_artifact_version_citation_id, `${path}.caused_by_artifact_version_citation_id`);
      const changeCause = nullableChangeCause(item.change_cause, `${path}.change_cause`);
      digest(item.actor_citation_id, `${path}.actor_citation_id`);
      const effectiveAt = nullableFixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
      fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
      const updatedAt = nullableFixedUtcTimestamp(item.updated_at, `${path}.updated_at`);
      nullableText(item.reason, `${path}.reason`, 2000);
      // updated_at and the ledger recorded_at cut are independent system
      // clocks; a transaction timestamp may precede its later ledger batch.
      const bindingStates = new Set(["fresh", "stale", "invalidated", "superseded", "pinned", "deferred"]);
      const decisionStates = new Set(DECISION_STATUSES);
      const lifecycleStateForEvent: Record<string, string> = {
        invalidated: "invalidated", superseded: "superseded", redacted: "redacted", abandoned: "abandoned",
        declared_inapplicable: "inapplicable", reactivated: "active",
      };
      if ((transitionKind === "binding" || transitionKind === "operation")
        && (!bindingStates.has(state) || changeCause === null || eventType !== null || causedBy !== null || updatedAt === null)) {
        fail(path, "binding/operation transition fields are inconsistent");
      }
      if (transitionKind === "decision_status"
        && (!decisionStates.has(state as ProofTraceDecisionStatus) || changeCause !== null || eventType !== null || causedBy !== null || effectiveAt === null || updatedAt !== null)) {
        fail(path, "decision status transition fields are inconsistent");
      }
      if (transitionKind === "artifact_lifecycle"
        && (eventType === null || lifecycleStateForEvent[eventType] !== state || changeCause !== null || effectiveAt === null || updatedAt !== null)) {
        fail(path, "artifact lifecycle transition fields are inconsistent");
      }
      break;
    }
    case "receipt_attestation": {
      const item = exactRecord(value, path, ["attestation_kind", "request_hash", "audit_event_id", "domain_event_id", "recorded_at"]);
      literal(item.attestation_kind, `${path}.attestation_kind`, ["operation_registration", "artifact_invalidation"] as const);
      digest(item.request_hash, `${path}.request_hash`);
      canonicalUuid(item.audit_event_id, `${path}.audit_event_id`);
      canonicalUuid(item.domain_event_id, `${path}.domain_event_id`);
      fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
      break;
    }
  }
  return value as JsonValue;
}

function embeddedCitationIds(value: JsonValue): Set<string> {
  const found = new Set<string>();
  const visit = (entry: JsonValue, key: string | null = null) => {
    if (Array.isArray(entry)) entry.forEach((child) => visit(child, key));
    else if (entry !== null && typeof entry === "object") Object.entries(entry).forEach(([childKey, child]) => visit(child, childKey));
    else if (typeof entry === "string" && key?.endsWith("_citation_id")) found.add(entry);
  };
  visit(value);
  return found;
}

function validateRootRef(value: unknown, path: string): ProofTraceArtifactVersionRef {
  const item = exactRecord(value, path, ["entity_kind", "id"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["artifact_version"]);
  canonicalUuid(item.id, `${path}.id`);
  return value as ProofTraceArtifactVersionRef;
}

function validateRequestRootRef(value: unknown, path: string): ProofTraceArtifactVersionRef {
  const item = exactRecord(value, path, ["entity_kind", "id"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["artifact_version"]);
  uuid(item.id, `${path}.id`);
  return value as ProofTraceArtifactVersionRef;
}

function validateLimits(value: unknown, path: string): ProofTraceLimits {
  const item = exactRecord(value, path, ["max_depth", "max_records", "max_paths", "max_path_steps"]);
  integer(item.max_depth, `${path}.max_depth`, 1, 32);
  integer(item.max_records, `${path}.max_records`, 1, 500);
  integer(item.max_paths, `${path}.max_paths`, 1, 5000);
  integer(item.max_path_steps, `${path}.max_path_steps`, 1, 10000);
  return value as ProofTraceLimits;
}

export function validateProofTraceRequest(value: unknown, path = "request"): asserts value is ProofTraceRequest {
  const item = exactRecord(
    value,
    path,
    ["project_id", "root_ref"],
    ["as_of", "recorded_as_of", "snapshot_token", "max_depth", "max_records", "max_paths", "max_path_steps"],
  );
  uuid(item.project_id, `${path}.project_id`);
  validateRequestRootRef(item.root_ref, `${path}.root_ref`);
  const effectiveAsOf = item.as_of !== undefined && item.as_of !== null
    ? awareTimestamp(item.as_of, `${path}.as_of`)
    : null;
  const hasRecordedAsOf = item.recorded_as_of !== undefined;
  const hasSnapshotToken = item.snapshot_token !== undefined;
  if (hasRecordedAsOf !== hasSnapshotToken) fail(path, "recorded_as_of and snapshot_token must be supplied together");
  if (hasRecordedAsOf) {
    const recordedAsOf = fixedUtcTimestamp(item.recorded_as_of, `${path}.recorded_as_of`);
    snapshotToken(item.snapshot_token, `${path}.snapshot_token`);
    if (effectiveAsOf === null) fail(`${path}.as_of`, "is required for frozen replay");
    if (compareInstantsAtMicrosecondPrecision(effectiveAsOf, recordedAsOf) > 0) {
      fail(`${path}.as_of`, "must not be later than recorded_as_of");
    }
  }
  if (item.max_depth !== undefined) integer(item.max_depth, `${path}.max_depth`, 1, 32);
  if (item.max_records !== undefined) integer(item.max_records, `${path}.max_records`, 1, 500);
  if (item.max_paths !== undefined) integer(item.max_paths, `${path}.max_paths`, 1, 5000);
  if (item.max_path_steps !== undefined) integer(item.max_path_steps, `${path}.max_path_steps`, 1, 10000);
}

export function normalizeProofTraceRequest(request: ProofTraceRequest): ProofTraceRequest {
  validateProofTraceRequest(request);
  return {
    ...request,
    ...(typeof request.as_of === "string" ? { as_of: normalizeInstantToUtcMicroseconds(request.as_of) } : {}),
    project_id: request.project_id.toLowerCase(),
    root_ref: {
      entity_kind: "artifact_version",
      id: request.root_ref.id.toLowerCase(),
    },
  };
}

function validateProofTraceExportRequest(value: unknown): asserts value is ProofTraceExportRequest {
  const item = exactRecord(
    value,
    "request",
    ["project_id", "root_ref", "as_of", "recorded_as_of", "snapshot_token", "expected_semantic_digest"],
    ["max_depth", "max_records", "max_paths", "max_path_steps"],
  );
  const displayRequest = { ...item };
  delete displayRequest.expected_semantic_digest;
  validateProofTraceRequest(displayRequest);
  awareTimestamp(item.as_of, "request.as_of");
  digest(item.expected_semantic_digest, "request.expected_semantic_digest");
}

function validateCitation(value: unknown, path: string): ProofTraceCitation {
  const base = record(value, path);
  const kind = literal(base.citation_kind, `${path}.citation_kind`, [
    "record", "decision_evidence", "operation_registration_receipt", "artifact_invalidation_receipt",
  ] as const);
  if (kind === "record") {
    const item = exactRecord(value, path, [
      "citation_kind", "citation_id", "record_kind", "id", "temporal_basis", "effective_at", "recorded_at",
    ]);
    digest(item.citation_id, `${path}.citation_id`);
    const recordKind = literal(item.record_kind, `${path}.record_kind`, RECORD_KINDS);
    canonicalUuid(item.id, `${path}.id`);
    const basis = literal(item.temporal_basis, `${path}.temporal_basis`, [
      "recorded_fact", "derived_from_owning_operation", "current_authorization_overlay",
    ] as const);
    const effectiveAt = item.effective_at === null ? null : fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
    const recordedAt = item.recorded_at === null ? null : fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
    const expectedBasis = recordKind === "tool"
      ? "derived_from_owning_operation"
      : recordKind === "actor"
        ? "current_authorization_overlay"
        : "recorded_fact";
    if (basis !== expectedBasis) fail(`${path}.temporal_basis`, `expected ${expectedBasis} for ${recordKind}`);
    if (basis === "recorded_fact" ? effectiveAt === null || recordedAt === null : effectiveAt !== null || recordedAt !== null) {
      fail(path, "recorded facts require both axes; derived and overlay citations require neither");
    }
  } else if (kind === "decision_evidence") {
    const item = exactRecord(value, path, [
      "citation_kind", "citation_id", "decision_id", "ordinal", "artifact_version_id", "effective_at", "recorded_at",
    ]);
    digest(item.citation_id, `${path}.citation_id`);
    canonicalUuid(item.decision_id, `${path}.decision_id`);
    integer(item.ordinal, `${path}.ordinal`);
    canonicalUuid(item.artifact_version_id, `${path}.artifact_version_id`);
    fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
    fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  } else {
    const specificKeys = kind === "operation_registration_receipt"
      ? ["operation_id"]
      : ["artifact_version_id", "lifecycle_event_id"];
    const item = exactRecord(value, path, [
      "citation_kind", "citation_id", "project_id", "idempotency_key", "request_hash",
      ...specificKeys, "audit_event_id", "domain_event_id", "recorded_at",
    ]);
    digest(item.citation_id, `${path}.citation_id`);
    canonicalUuid(item.project_id, `${path}.project_id`);
    const idempotencyKey = text(item.idempotency_key, `${path}.idempotency_key`);
    if (IDEMPOTENCY_BOUNDARY_WHITESPACE_PATTERN.test(idempotencyKey) || [...idempotencyKey].length > 200) {
      fail(`${path}.idempotency_key`, "expected a trimmed string of at most 200 characters");
    }
    digest(item.request_hash, `${path}.request_hash`);
    canonicalUuid(item.audit_event_id, `${path}.audit_event_id`);
    canonicalUuid(item.domain_event_id, `${path}.domain_event_id`);
    fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
    if (kind === "operation_registration_receipt") canonicalUuid(item.operation_id, `${path}.operation_id`);
    if (kind === "artifact_invalidation_receipt") {
      canonicalUuid(item.artifact_version_id, `${path}.artifact_version_id`);
      canonicalUuid(item.lifecycle_event_id, `${path}.lifecycle_event_id`);
    }
  }
  return value as ProofTraceCitation;
}

function validateTerminal(value: unknown, path: string): ProofTraceTerminal {
  const item = exactRecord(value, path, ["terminal_kind", "reason", "supporting_citation_ids"]);
  const kind = literal(item.terminal_kind, `${path}.terminal_kind`, [
    "source_operation", "source_value", "cycle", "ambiguous_producer", "storage_gap", "truncated",
  ] as const);
  const reasons = kind === "source_operation"
    ? ["zero_input_operation"] as const
    : kind === "source_value"
      ? ["producer_absent_after_complete_probe"] as const
    : kind === "cycle"
      ? ["cycle_detected"] as const
      : kind === "ambiguous_producer"
        ? ["ambiguous_producer"] as const
        : kind === "storage_gap"
          ? [
              "state_history_incomplete", "effective_time_unavailable", "native_receipt_unavailable",
              "bundle_receipt_unavailable", "semantic_payload_budget_exceeded", "unsupported_semantics",
            ] as const
          : [
              "max_depth_reached", "max_records_reached", "max_paths_reached", "max_path_steps_reached",
              "document_size_limit_reached",
            ] as const;
  literal(item.reason, `${path}.reason`, reasons);
  const supportingCitationIds = sortedUniqueDigests(item.supporting_citation_ids, `${path}.supporting_citation_ids`, 0, 100);
  const minimumSupport = kind === "ambiguous_producer" ? 2 : kind === "source_operation" || kind === "source_value" || kind === "cycle" ? 1 : 0;
  if (supportingCitationIds.length < minimumSupport) fail(`${path}.supporting_citation_ids`, `expected at least ${minimumSupport} visible support citation${minimumSupport === 1 ? "" : "s"}`);
  return value as ProofTraceTerminal;
}

function validateDocument(value: unknown, path: string): ProofTraceDocument {
  const item = exactRecord(value, path, ["contract_version", "snapshot", "paths", "assertions", "citations", "completeness"]);
  literal(item.contract_version, `${path}.contract_version`, [PROOF_TRACE_CONTRACT_VERSION]);
  const snapshot = exactRecord(item.snapshot, `${path}.snapshot`, [
    "project_id", "root_ref", "effective_as_of", "recorded_as_of", "snapshot_token",
    "temporal_basis", "source_contract_version", "limits",
  ]);
  canonicalUuid(snapshot.project_id, `${path}.snapshot.project_id`);
  validateRootRef(snapshot.root_ref, `${path}.snapshot.root_ref`);
  const effectiveAsOf = fixedUtcTimestamp(snapshot.effective_as_of, `${path}.snapshot.effective_as_of`);
  const recordedAsOf = fixedUtcTimestamp(snapshot.recorded_as_of, `${path}.snapshot.recorded_as_of`);
  if (compareInstantsAtMicrosecondPrecision(effectiveAsOf, recordedAsOf) > 0) {
    fail(`${path}.snapshot.effective_as_of`, "must not be later than recorded_as_of");
  }
  snapshotToken(snapshot.snapshot_token, `${path}.snapshot.snapshot_token`);
  literal(snapshot.temporal_basis, `${path}.snapshot.temporal_basis`, ["effective_and_recorded_time"]);
  literal(snapshot.source_contract_version, `${path}.snapshot.source_contract_version`, ["thread.lineage.v2"]);
  validateLimits(snapshot.limits, `${path}.snapshot.limits`);

  const citations = boundedArray(item.citations, `${path}.citations`, 10000).map((citation, index) => (
    validateCitation(citation, `${path}.citations[${index}]`)
  ));
  const citationIds = citations.map((citation) => citation.citation_id);
  if (new Set(citationIds).size !== citationIds.length || citationIds.some((id, index) => index > 0 && citationIds[index - 1]! > id)) {
    fail(`${path}.citations`, "citation_id values must be unique and sorted");
  }
  const citationSet = new Set(citationIds);
  citations.forEach((citation, index) => {
    if (
      (citation.citation_kind === "operation_registration_receipt" || citation.citation_kind === "artifact_invalidation_receipt")
      && citation.project_id !== snapshot.project_id
    ) {
      fail(`${path}.citations[${index}].project_id`, "must equal snapshot.project_id");
    }
  });

  const semanticBudget: SemanticPayloadBudget = { nodes: 0, canonicalBytes: 0 };
  const assertions = boundedArray(item.assertions, `${path}.assertions`, 10000).map((assertion, index) => {
    const assertionPath = `${path}.assertions[${index}]`;
    const assertionItem = exactRecord(assertion, assertionPath, [
      "assertion_id", "ordinal", "assertion_kind", "subject_citation_id", "value", "supporting_citation_ids",
    ]);
    digest(assertionItem.assertion_id, `${assertionPath}.assertion_id`);
    if (integer(assertionItem.ordinal, `${assertionPath}.ordinal`) !== index) fail(`${assertionPath}.ordinal`, "must match array order");
    const assertionKind = literal(assertionItem.assertion_kind, `${assertionPath}.assertion_kind`, ASSERTION_KINDS);
    const subject = digest(assertionItem.subject_citation_id, `${assertionPath}.subject_citation_id`);
    if (!citationSet.has(subject)) fail(`${assertionPath}.subject_citation_id`, "must reference a citation");
    const assertionValue = validateAssertionValue(assertionKind, assertionItem.value, `${assertionPath}.value`, semanticBudget);
    const supporting = sortedUniqueDigests(assertionItem.supporting_citation_ids, `${assertionPath}.supporting_citation_ids`, 1, 100);
    if (!supporting.includes(subject)) fail(`${assertionPath}.supporting_citation_ids`, "must include the assertion subject");
    const expectedSupport = new Set([subject, ...embeddedCitationIds(assertionValue)]);
    if (supporting.length !== expectedSupport.size || supporting.some((citationId) => !expectedSupport.has(citationId))) {
      fail(`${assertionPath}.supporting_citation_ids`, "must exactly cover the subject and cited assertion values");
    }
    supporting.forEach((citationId, citationIndex) => {
      if (!citationSet.has(citationId)) fail(`${assertionPath}.supporting_citation_ids[${citationIndex}]`, "must reference a citation");
    });
    return assertion as ProofTraceAssertion;
  });
  const assertionIds = assertions.map((assertion) => assertion.assertion_id);
  if (new Set(assertionIds).size !== assertionIds.length) fail(`${path}.assertions`, "assertion_id values must be unique");
  const assertionRank = new Map(ASSERTION_KINDS.map((kind, index) => [kind, index]));
  const assertionSortKeys = assertions.map((assertion) => `${assertion.subject_citation_id}\u0000${String(assertionRank.get(assertion.assertion_kind)).padStart(2, "0")}\u0000${assertion.assertion_id}`);
  if (assertionSortKeys.some((key, index) => index > 0 && assertionSortKeys[index - 1]! > key)) {
    fail(`${path}.assertions`, "must follow subject and assertion-kind order");
  }
  const assertionSet = new Set(assertionIds);
  const citationById = new Map(citations.map((citation) => [citation.citation_id, citation]));
  const assertionsBySubject = new Map<string, ProofTraceAssertion[]>();
  const assertionsBySubjectAndKind = new Map<string, Map<ProofTraceAssertionKind, ProofTraceAssertion[]>>();
  assertions.forEach((assertion) => {
    const subjectAssertions = assertionsBySubject.get(assertion.subject_citation_id) ?? [];
    subjectAssertions.push(assertion);
    assertionsBySubject.set(assertion.subject_citation_id, subjectAssertions);

    const subjectKinds = assertionsBySubjectAndKind.get(assertion.subject_citation_id)
      ?? new Map<ProofTraceAssertionKind, ProofTraceAssertion[]>();
    const kindAssertions = subjectKinds.get(assertion.assertion_kind) ?? [];
    kindAssertions.push(assertion);
    subjectKinds.set(assertion.assertion_kind, kindAssertions);
    assertionsBySubjectAndKind.set(assertion.subject_citation_id, subjectKinds);
  });
  const assertionsOfKind = <K extends ProofTraceAssertionKind>(citationId: string, assertionKind: K) => (
    (assertionsBySubjectAndKind.get(citationId)?.get(assertionKind) ?? []) as Extract<ProofTraceAssertion, { assertion_kind: K }>[]
  );
  const requireRecordCitation = (citationId: string, kinds: readonly ProofTraceRecordKind[], assertionPath: string): ProofTraceRecordCitation => {
    const citation = citationById.get(citationId);
    if (citation?.citation_kind !== "record" || !kinds.includes(citation.record_kind)) {
      fail(assertionPath, `requires a ${kinds.join("/")} record citation`);
    }
    return citation;
  };
  assertions.forEach((assertion, index) => {
    const assertionPath = `${path}.assertions[${index}]`;
    switch (assertion.assertion_kind) {
      case "artifact_identity":
        requireRecordCitation(assertion.subject_citation_id, ["artifact"], assertionPath);
        requireRecordCitation(assertion.value.creator_citation_id, ["actor"], assertionPath);
        break;
      case "artifact_version_identity":
        requireRecordCitation(assertion.subject_citation_id, ["artifact_version"], assertionPath);
        requireRecordCitation(assertion.value.artifact_citation_id, ["artifact"], assertionPath);
        requireRecordCitation(assertion.value.creator_citation_id, ["actor"], assertionPath);
        break;
      case "artifact_value": case "content_identity": case "lifecycle_validity": case "physical_semantics_references":
        requireRecordCitation(assertion.subject_citation_id, ["artifact_version"], assertionPath);
        break;
      case "binding_semantics":
        requireRecordCitation(assertion.subject_citation_id, ["binding"], assertionPath);
        requireRecordCitation(assertion.value.operation_citation_id, ["operation_run"], assertionPath);
        requireRecordCitation(assertion.value.artifact_version_citation_id, ["artifact_version"], assertionPath);
        if (assertion.value.state.event_citation_id) requireRecordCitation(assertion.value.state.event_citation_id, ["binding_state_event"], assertionPath);
        if (assertion.value.state.actor_citation_id) requireRecordCitation(assertion.value.state.actor_citation_id, ["actor"], assertionPath);
        break;
      case "operation_spec_definition":
        requireRecordCitation(assertion.subject_citation_id, ["operation_spec"], assertionPath);
        break;
      case "operation_invocation":
        requireRecordCitation(assertion.subject_citation_id, ["operation_run"], assertionPath);
        requireRecordCitation(assertion.value.operation_spec_citation_id, ["operation_spec"], assertionPath);
        requireRecordCitation(assertion.value.actor_citation_id, ["actor"], assertionPath);
        requireRecordCitation(assertion.value.tool_citation_id, ["tool"], assertionPath);
        break;
      case "operation_parameters": case "operation_environment": case "operation_assumptions":
        requireRecordCitation(assertion.subject_citation_id, ["operation_run"], assertionPath);
        break;
      case "tool_snapshot":
        requireRecordCitation(assertion.subject_citation_id, ["tool"], assertionPath);
        break;
      case "actor_identity":
        requireRecordCitation(assertion.subject_citation_id, ["actor"], assertionPath);
        break;
      case "engineering_decision":
        requireRecordCitation(assertion.subject_citation_id, ["decision"], assertionPath);
        requireRecordCitation(assertion.value.artifact_version_citation_id, ["artifact_version"], assertionPath);
        requireRecordCitation(assertion.value.creator_citation_id, ["actor"], assertionPath);
        break;
      case "decision_evidence": {
        const subject = citationById.get(assertion.subject_citation_id);
        const decision = requireRecordCitation(assertion.value.decision_citation_id, ["decision"], assertionPath);
        const evidence = requireRecordCitation(assertion.value.artifact_version_citation_id, ["artifact_version"], assertionPath);
        if (subject?.citation_kind !== "decision_evidence" || subject.decision_id !== decision.id
          || subject.artifact_version_id !== evidence.id
          || subject.effective_at !== assertion.value.effective_at
          || subject.recorded_at !== assertion.value.recorded_at) {
          fail(assertionPath, "decision evidence assertion must equal its evidence citation");
        }
        break;
      }
      case "supersession":
        requireRecordCitation(assertion.subject_citation_id, ["supersession"], assertionPath);
        requireRecordCitation(assertion.value.old_artifact_version_citation_id, ["artifact_version"], assertionPath);
        requireRecordCitation(assertion.value.new_artifact_version_citation_id, ["artifact_version"], assertionPath);
        requireRecordCitation(assertion.value.creator_citation_id, ["actor"], assertionPath);
        if (assertion.value.operation_citation_id) requireRecordCitation(assertion.value.operation_citation_id, ["operation_run"], assertionPath);
        break;
      case "merge_selection":
        requireRecordCitation(assertion.subject_citation_id, ["merge_input"], assertionPath);
        requireRecordCitation(assertion.value.merge_operation_citation_id, ["operation_run"], assertionPath);
        requireRecordCitation(assertion.value.source_operation_citation_id, ["operation_run"], assertionPath);
        break;
      case "state_transition": {
        const [subjectKind, targetKind] = ({
          binding: ["binding_state_event", "binding"], operation: ["operation_state_event", "operation_run"],
          artifact_lifecycle: ["lifecycle_event", "artifact_version"], decision_status: ["decision_status_event", "decision"],
        } as const)[assertion.value.transition_kind];
        requireRecordCitation(assertion.subject_citation_id, [subjectKind], assertionPath);
        requireRecordCitation(assertion.value.target_citation_id, [targetKind], assertionPath);
        requireRecordCitation(assertion.value.actor_citation_id, ["actor"], assertionPath);
        if (assertion.value.caused_by_artifact_version_citation_id) {
          requireRecordCitation(assertion.value.caused_by_artifact_version_citation_id, ["artifact_version"], assertionPath);
        }
        break;
      }
      case "receipt_attestation": {
        const receipt = citationById.get(assertion.subject_citation_id);
        const expectedKind = assertion.value.attestation_kind === "operation_registration" ? "operation_registration_receipt" : "artifact_invalidation_receipt";
        if (receipt?.citation_kind !== expectedKind || receipt.request_hash !== assertion.value.request_hash || receipt.audit_event_id !== assertion.value.audit_event_id
          || receipt.domain_event_id !== assertion.value.domain_event_id || receipt.recorded_at !== assertion.value.recorded_at) {
          fail(assertionPath, "receipt assertion must equal its receipt citation");
        }
        break;
      }
    }
  });

  const requirePathRecord = (step: ProofTraceStep, recordKind: ProofTraceRecordKind, stepPath: string): ProofTraceRecordCitation => {
    const citation = citationById.get(step.citation_id);
    if (citation?.citation_kind !== "record" || citation.record_kind !== recordKind) {
      fail(`${stepPath}.citation_id`, `${step.step_role} requires a ${recordKind} record citation`);
    }
    return citation;
  };
  const requirePathRole = (step: ProofTraceStep, role: ProofTraceStepRole, stepPath: string): void => {
    if (step.step_role !== role) fail(`${stepPath}.step_role`, `expected ${role} in the source-to-root path grammar`);
  };
  const bindingSemanticsFor = (step: ProofTraceStep, direction: "input" | "output", stepPath: string) => {
    requirePathRecord(step, "binding", stepPath);
    const matches = assertionsOfKind(step.citation_id, "binding_semantics");
    if (matches.length !== 1 || matches[0]!.value.direction !== direction) {
      fail(stepPath, `${direction}_binding requires exactly one matching binding_semantics assertion`);
    }
    return matches[0]!.value;
  };
  const validateOutputJoin = (
    operation: ProofTraceStep,
    outputBinding: ProofTraceStep,
    nextValue: ProofTraceStep,
    stepPath: string,
  ): void => {
    requirePathRecord(operation, "operation_run", `${stepPath}.operation`);
    requirePathRecord(nextValue, "artifact_version", `${stepPath}.next_value`);
    const output = bindingSemanticsFor(outputBinding, "output", `${stepPath}.output_binding`);
    if (output.operation_citation_id !== operation.citation_id || output.artifact_version_citation_id !== nextValue.citation_id) {
      fail(stepPath, "output binding must join the same operation to the next ArtifactVersion");
    }
  };
  const validateTransformationJoin = (
    previousValue: ProofTraceStep,
    inputBinding: ProofTraceStep,
    operation: ProofTraceStep,
    outputBinding: ProofTraceStep,
    nextValue: ProofTraceStep,
    stepPath: string,
  ): void => {
    requirePathRecord(previousValue, "artifact_version", `${stepPath}.previous_value`);
    requirePathRecord(operation, "operation_run", `${stepPath}.operation`);
    requirePathRecord(nextValue, "artifact_version", `${stepPath}.next_value`);
    const input = bindingSemanticsFor(inputBinding, "input", `${stepPath}.input_binding`);
    const output = bindingSemanticsFor(outputBinding, "output", `${stepPath}.output_binding`);
    if (input.operation_citation_id !== operation.citation_id || input.artifact_version_citation_id !== previousValue.citation_id) {
      fail(stepPath, "input binding must join the previous ArtifactVersion to the same operation");
    }
    if (output.operation_citation_id !== operation.citation_id || output.artifact_version_citation_id !== nextValue.citation_id) {
      fail(stepPath, "output binding must join the same operation to the next ArtifactVersion");
    }
  };

  const pathOperationCitationIds = new Set<string>();
  const pathBindingSemantics = new Map<string, Extract<ProofTraceAssertion, { assertion_kind: "binding_semantics" }>["value"]>();
  const sourceOperationCitationIds = new Set<string>();
  const exactTerminalSupport = (terminal: ProofTraceTerminal, expected: Set<string>, terminalPath: string): void => {
    if (terminal.supporting_citation_ids.length !== expected.size
      || terminal.supporting_citation_ids.some((citationId) => !expected.has(citationId))) {
      fail(`${terminalPath}.supporting_citation_ids`, "must contain exactly the citations required by its terminal semantics");
    }
  };

  let totalPathSteps = 0;
  const paths = boundedArray(item.paths, `${path}.paths`, 5000, 1).map((proofPath, index) => {
    const proofPathPath = `${path}.paths[${index}]`;
    const pathItem = exactRecord(proofPath, proofPathPath, ["ordinal", "path_id", "steps", "terminal"]);
    if (integer(pathItem.ordinal, `${proofPathPath}.ordinal`) !== index) fail(`${proofPathPath}.ordinal`, "must match array order");
    digest(pathItem.path_id, `${proofPathPath}.path_id`);
    const steps = boundedArray(pathItem.steps, `${proofPathPath}.steps`, 1000, 1);
    const validatedSteps = steps.map((step, stepIndex) => {
      const stepPath = `${proofPathPath}.steps[${stepIndex}]`;
      const stepItem = exactRecord(step, stepPath, ["ordinal", "step_role", "citation_id", "assertion_ids"]);
      if (integer(stepItem.ordinal, `${stepPath}.ordinal`) !== stepIndex) fail(`${stepPath}.ordinal`, "must match array order");
      literal(stepItem.step_role, `${stepPath}.step_role`, STEP_ROLES);
      const citationId = digest(stepItem.citation_id, `${stepPath}.citation_id`);
      if (!citationSet.has(citationId)) fail(`${stepPath}.citation_id`, "must reference a citation");
      sortedUniqueDigests(stepItem.assertion_ids, `${stepPath}.assertion_ids`, 0, 100).forEach((assertionId, assertionIndex) => {
        if (!assertionSet.has(assertionId)) fail(`${stepPath}.assertion_ids[${assertionIndex}]`, "must reference an assertion");
      });
      return step as ProofTraceStep;
    });
    totalPathSteps += validatedSteps.length;
    if (totalPathSteps > (snapshot.limits as ProofTraceLimits).max_path_steps) {
      fail(`${proofPathPath}.steps`, "proof paths cannot exceed snapshot.limits.max_path_steps in total");
    }
    const terminal = validateTerminal(pathItem.terminal, `${proofPathPath}.terminal`);
    terminal.supporting_citation_ids.forEach((citationId, citationIndex) => {
      if (!citationSet.has(citationId)) fail(`${proofPathPath}.terminal.supporting_citation_ids[${citationIndex}]`, "must reference a citation");
    });
    const selectedStep = validatedSteps[validatedSteps.length - 1]!;
    if (selectedStep.step_role !== "selected_value") fail(`${proofPathPath}.steps[${steps.length - 1}].step_role`, "every path must end at the selected value");
    const selectedCitation = citationById.get(selectedStep.citation_id);
    const rootRef = snapshot.root_ref as ProofTraceArtifactVersionRef;
    if (selectedCitation?.citation_kind !== "record" || selectedCitation.record_kind !== "artifact_version" || selectedCitation.id !== rootRef.id) {
      fail(`${proofPathPath}.steps[${steps.length - 1}].citation_id`, "selected value must be the snapshot ArtifactVersion root");
    }

    let groupStart: number;
    if (terminal.terminal_kind === "source_operation") {
      if (validatedSteps.length < 3) fail(`${proofPathPath}.steps`, "a zero-input source operation path requires operation, output binding, and value steps");
      requirePathRole(validatedSteps[0]!, "source_invocation", `${proofPathPath}.steps[0]`);
      requirePathRole(validatedSteps[1]!, "output_binding", `${proofPathPath}.steps[1]`);
      requirePathRole(validatedSteps[2]!, validatedSteps.length === 3 ? "selected_value" : "intermediate_value", `${proofPathPath}.steps[2]`);
      validateOutputJoin(validatedSteps[0]!, validatedSteps[1]!, validatedSteps[2]!, `${proofPathPath}.steps[0:3]`);
      pathOperationCitationIds.add(validatedSteps[0]!.citation_id);
      sourceOperationCitationIds.add(validatedSteps[0]!.citation_id);
      pathBindingSemantics.set(
        validatedSteps[1]!.citation_id,
        bindingSemanticsFor(validatedSteps[1]!, "output", `${proofPathPath}.steps[1]`),
      );
      exactTerminalSupport(terminal, new Set([validatedSteps[0]!.citation_id]), `${proofPathPath}.terminal`);
      groupStart = 3;
    } else if (terminal.terminal_kind === "source_value") {
      requirePathRole(validatedSteps[0]!, validatedSteps.length === 1 ? "selected_value" : "source_value", `${proofPathPath}.steps[0]`);
      requirePathRecord(validatedSteps[0]!, "artifact_version", `${proofPathPath}.steps[0]`);
      exactTerminalSupport(terminal, new Set([validatedSteps[0]!.citation_id]), `${proofPathPath}.terminal`);
      groupStart = 1;
    } else {
      requirePathRole(validatedSteps[0]!, validatedSteps.length === 1 ? "selected_value" : "intermediate_value", `${proofPathPath}.steps[0]`);
      requirePathRecord(validatedSteps[0]!, "artifact_version", `${proofPathPath}.steps[0]`);
      if (terminal.terminal_kind === "cycle") {
        exactTerminalSupport(terminal, new Set([validatedSteps[0]!.citation_id]), `${proofPathPath}.terminal`);
      } else if (terminal.terminal_kind === "ambiguous_producer") {
        terminal.supporting_citation_ids.forEach((citationId, supportIndex) => {
          const supportPath = `${proofPathPath}.terminal.supporting_citation_ids[${supportIndex}]`;
          const citation = citationById.get(citationId);
          if (citation?.citation_kind !== "record" || citation.record_kind !== "binding") {
            fail(supportPath, "ambiguous producer support must cite a binding record");
          }
          const semantics = bindingSemanticsFor(
            { ordinal: 0, step_role: "output_binding", citation_id: citationId, assertion_ids: [] },
            "output",
            supportPath,
          );
          if (semantics.artifact_version_citation_id !== validatedSteps[0]!.citation_id) {
            fail(supportPath, "ambiguous producer binding must produce the visible boundary ArtifactVersion");
          }
        });
      }
      groupStart = 1;
    }
    if ((validatedSteps.length - groupStart) % 4 !== 0) {
      fail(`${proofPathPath}.steps`, "path must continue in input-binding, transformation, output-binding, value groups");
    }
    for (let group = groupStart; group < validatedSteps.length; group += 4) {
      const previousValue = validatedSteps[group - 1]!;
      const inputBinding = validatedSteps[group]!;
      const operation = validatedSteps[group + 1]!;
      const outputBinding = validatedSteps[group + 2]!;
      const nextValue = validatedSteps[group + 3]!;
      requirePathRole(inputBinding, "input_binding", `${proofPathPath}.steps[${group}]`);
      requirePathRole(operation, "transformation", `${proofPathPath}.steps[${group + 1}]`);
      requirePathRole(outputBinding, "output_binding", `${proofPathPath}.steps[${group + 2}]`);
      requirePathRole(nextValue, group + 4 === validatedSteps.length ? "selected_value" : "intermediate_value", `${proofPathPath}.steps[${group + 3}]`);
      validateTransformationJoin(previousValue, inputBinding, operation, outputBinding, nextValue, `${proofPathPath}.steps[${group - 1}:${group + 4}]`);
      pathOperationCitationIds.add(operation.citation_id);
      pathBindingSemantics.set(
        inputBinding.citation_id,
        bindingSemanticsFor(inputBinding, "input", `${proofPathPath}.steps[${group}]`),
      );
      pathBindingSemantics.set(
        outputBinding.citation_id,
        bindingSemanticsFor(outputBinding, "output", `${proofPathPath}.steps[${group + 2}]`),
      );
    }
    return proofPath as ProofTracePath;
  });
  const pathIds = paths.map((proofPath) => proofPath.path_id);
  if (paths.length > (snapshot.limits as ProofTraceLimits).max_paths) fail(`${path}.paths`, "cannot exceed snapshot.limits.max_paths");
  if (new Set(pathIds).size !== paths.length || pathIds.some((id, index) => index > 0 && pathIds[index - 1]! > id)) {
    fail(`${path}.paths`, "path_id values must be unique and sorted");
  }

  const bindingsByOperation = new Map<string, Map<string, Extract<ProofTraceAssertion, { assertion_kind: "binding_semantics" }>["value"]>>();
  pathBindingSemantics.forEach((semantics, citationId) => {
    const bindings = bindingsByOperation.get(semantics.operation_citation_id) ?? new Map();
    bindings.set(citationId, semantics);
    bindingsByOperation.set(semantics.operation_citation_id, bindings);
  });
  let pathMissingRequiredInput = false;
  pathOperationCitationIds.forEach((operationCitationId) => {
    const invocationAssertions = assertionsOfKind(operationCitationId, "operation_invocation");
    if (invocationAssertions.length !== 1) {
      fail(`${path}.paths`, "each path operation requires exactly one operation_invocation assertion");
    }
    const invocation = invocationAssertions[0]!.value;
    const specAssertions = assertionsOfKind(invocation.operation_spec_citation_id, "operation_spec_definition");
    if (specAssertions.length !== 1) {
      fail(`${path}.paths`, "each path operation requires exactly one operation_spec_definition assertion");
    }
    const spec = specAssertions[specAssertions.length - 1]!.value;
    const contracts = {
      input: new Map(spec.input_roles.map((contract) => [contract.role, contract])),
      output: new Map(spec.output_roles.map((contract) => [contract.role, contract])),
    };
    const counts = new Map<string, number>();
    let inputBindingCount = 0;
    bindingsByOperation.get(operationCitationId)?.forEach((semantics) => {
      if (!contracts[semantics.direction].has(semantics.role)) {
        fail(`${path}.paths`, "proof path binding role is not declared by its operation spec");
      }
      const countKey = `${semantics.direction}\u0000${semantics.role}`;
      counts.set(countKey, (counts.get(countKey) ?? 0) + 1);
      if (semantics.direction === "input") inputBindingCount += 1;
    });
    if (sourceOperationCitationIds.has(operationCitationId)
      && (inputBindingCount !== 0 || spec.input_roles.some((contract) => contract.min_count !== 0))) {
      fail(`${path}.paths`, "zero-input source operation cannot require or cite inputs");
    }
    for (const [direction, roleContracts] of Object.entries(contracts) as ["input" | "output", Map<string, ProofTraceRoleContractValue>][]) {
      roleContracts.forEach((contract, role) => {
        const count = counts.get(`${direction}\u0000${role}`) ?? 0;
        if (count > contract.max_count || (direction === "output" && count < contract.min_count)) {
          fail(`${path}.paths`, "proof path binding counts violate the operation spec");
        }
        if (direction === "input" && count < contract.min_count) pathMissingRequiredInput = true;
      });
    }
  });

  const completeness = exactRecord(item.completeness, `${path}.completeness`, [
    "traversal", "evidence", "temporal", "gaps", "redaction_terminal",
  ]);
  literal(completeness.traversal, `${path}.completeness.traversal`, ["complete", "incomplete"]);
  literal(completeness.evidence, `${path}.completeness.evidence`, ["complete", "incomplete"]);
  literal(completeness.temporal, `${path}.completeness.temporal`, ["effective_and_recorded_time"]);
  const gapKeys: string[] = [];
  const gapCodes = new Set<string>();
  const gapCitationIds = new Set<string>();
  let hasGlobalSemanticPayloadGap = false;
  boundedArray(completeness.gaps, `${path}.completeness.gaps`, 1000).forEach((gap, gapIndex) => {
    const gapPath = `${path}.completeness.gaps[${gapIndex}]`;
    const gapItem = exactRecord(gap, gapPath, ["code", "subject_citation_id"]);
    literal(gapItem.code, `${gapPath}.code`, GAP_CODES);
    if (gapItem.subject_citation_id !== null) {
      const citationId = digest(gapItem.subject_citation_id, `${gapPath}.subject_citation_id`);
      if (!citationSet.has(citationId)) fail(`${gapPath}.subject_citation_id`, "must reference a citation");
      gapCitationIds.add(citationId);
    }
    if (gapItem.code === "semantic_payload_budget_exceeded" && gapItem.subject_citation_id === null) {
      hasGlobalSemanticPayloadGap = true;
    }
    gapCodes.add(String(gapItem.code));
    gapKeys.push(`${String(gapItem.code)}\u0000${String(gapItem.subject_citation_id ?? "")}`);
  });
  if (new Set(gapKeys).size !== gapKeys.length || gapKeys.some((key, index) => index > 0 && gapKeys[index - 1]! > key)) {
    fail(`${path}.completeness.gaps`, "gaps must be unique and sorted by code and subject");
  }
  if (pathMissingRequiredInput && !gapCodes.has("max_paths_reached") && !gapCodes.has("max_path_steps_reached")) {
    fail(`${path}.paths`, "proof path binding counts violate the operation spec");
  }
  if (completeness.redaction_terminal !== null) {
    const redaction = exactRecord(completeness.redaction_terminal, `${path}.completeness.redaction_terminal`, ["terminal_kind", "reason"]);
    literal(redaction.terminal_kind, `${path}.completeness.redaction_terminal.terminal_kind`, ["redacted"]);
    literal(redaction.reason, `${path}.completeness.redaction_terminal.reason`, ["restricted_evidence_omitted"]);
    if (paths.some((proofPath) => proofPath.terminal.terminal_kind === "source_value")) {
      fail(`${path}.paths`, "redacted proof cannot assert producer absence as a source value");
    }
  }
  const traversalGapCodes = new Set([
    "max_depth_reached", "max_records_reached", "max_paths_reached", "max_path_steps_reached", "cycle_detected", "ambiguous_producer",
    "document_size_limit_reached", "unknown_record_type", "unknown_relationship_type", "unsupported_semantics",
  ]);
  const hasIncompletePath = paths.some((proofPath) => ["cycle", "ambiguous_producer", "storage_gap", "truncated"].includes(proofPath.terminal.terminal_kind));
  const expectedTraversal = gapKeys.some((key) => traversalGapCodes.has(key.split("\u0000", 1)[0]!))
    || hasGlobalSemanticPayloadGap || completeness.redaction_terminal !== null || hasIncompletePath
    ? "incomplete"
    : "complete";
  const expectedEvidence = gapKeys.length > 0 || completeness.redaction_terminal !== null ? "incomplete" : "complete";
  if (completeness.traversal !== expectedTraversal) fail(`${path}.completeness.traversal`, "must equal its traversal gaps and redaction terminal");
  if (completeness.evidence !== expectedEvidence) fail(`${path}.completeness.evidence`, "must equal its gaps and redaction terminal");

  const usedAssertionIds = new Set<string>();
  paths.forEach((proofPath) => proofPath.steps.forEach((step) => {
    step.assertion_ids.forEach((assertionId) => usedAssertionIds.add(assertionId));
  }));
  if (usedAssertionIds.size !== assertionSet.size || [...assertionSet].some((assertionId) => !usedAssertionIds.has(assertionId))) {
    fail(`${path}.assertions`, "assertions must not be orphaned from visible paths");
  }
  const usedCitationIds = new Set<string>(gapCitationIds);
  paths.forEach((proofPath) => {
    proofPath.steps.forEach((step) => usedCitationIds.add(step.citation_id));
    proofPath.terminal.supporting_citation_ids.forEach((citationId) => usedCitationIds.add(citationId));
  });
  assertions.forEach((assertion) => {
    usedCitationIds.add(assertion.subject_citation_id);
    assertion.supporting_citation_ids.forEach((citationId) => usedCitationIds.add(citationId));
  });
  if (usedCitationIds.size !== citationSet.size || [...citationSet].some((citationId) => !usedCitationIds.has(citationId))) {
    fail(`${path}.citations`, "citations must not carry unrelated records");
  }
  const actorCitationIds = citations
    .filter((citation): citation is ProofTraceRecordCitation => citation.citation_kind === "record" && citation.record_kind === "actor")
    .map((citation) => citation.citation_id);
  if (actorCitationIds.some((citationId) => {
    const subjectAssertions = assertionsBySubject.get(citationId) ?? [];
    return subjectAssertions.length !== 1 || subjectAssertions[0]!.assertion_kind !== "actor_identity";
  })) {
    fail(`${path}.assertions`, "every actor citation requires exactly one actor identity assertion");
  }
  citations.forEach((citation, citationIndex) => {
    const requiredKinds: readonly ProofTraceAssertionKind[] = citation.citation_kind === "record"
      ? ({
          operation_run: ["operation_invocation", "operation_parameters", "operation_environment", "operation_assumptions"],
          operation_spec: ["operation_spec_definition"],
          tool: ["tool_snapshot"],
          binding: ["binding_semantics"],
          decision: ["engineering_decision"],
        } as Partial<Record<ProofTraceRecordKind, readonly ProofTraceAssertionKind[]>>)[citation.record_kind] ?? []
      : citation.citation_kind === "decision_evidence"
        ? ["decision_evidence"]
        : ["receipt_attestation"];
    requiredKinds.forEach((assertionKind) => {
      if (assertionsOfKind(citation.citation_id, assertionKind).length !== 1) {
        fail(`${path}.citations[${citationIndex}]`, `requires exactly one ${assertionKind} assertion`);
      }
    });
  });
  const terminalGapCodes = paths
    .filter((proofPath) => ["cycle", "ambiguous_producer", "storage_gap", "truncated"].includes(proofPath.terminal.terminal_kind))
    .map((proofPath) => proofPath.terminal.reason);
  if (terminalGapCodes.some((code) => !gapCodes.has(code))) {
    fail(`${path}.completeness.gaps`, "every incomplete path terminal requires a proof gap");
  }

  const effectiveCut = snapshot.effective_as_of as string;
  const recordedCut = snapshot.recorded_as_of as string;
  const effectiveAtCut = (value: string | null, valuePath: string) => {
    if (value !== null && value > effectiveCut) fail(valuePath, "must not exceed snapshot.effective_as_of");
  };
  const recordedAtCut = (value: string | null, valuePath: string) => {
    if (value !== null && value > recordedCut) fail(valuePath, "must not exceed snapshot.recorded_as_of");
  };
  citations.forEach((citation, index) => {
    const citationPath = `${path}.citations[${index}]`;
    if (citation.citation_kind === "decision_evidence") {
      effectiveAtCut(citation.effective_at, `${citationPath}.effective_at`);
      recordedAtCut(citation.recorded_at, `${citationPath}.recorded_at`);
    } else if (citation.citation_kind === "record") {
      effectiveAtCut(citation.effective_at, `${citationPath}.effective_at`);
      recordedAtCut(citation.recorded_at, `${citationPath}.recorded_at`);
    } else {
      recordedAtCut(citation.recorded_at, `${citationPath}.recorded_at`);
    }
  });
  assertions.forEach((assertion, index) => {
    const valuePath = `${path}.assertions[${index}].value`;
    switch (assertion.assertion_kind) {
      case "artifact_identity":
      case "artifact_version_identity":
      case "supersession":
      case "merge_selection":
        effectiveAtCut(assertion.value.created_at, `${valuePath}.created_at`);
        break;
      case "binding_semantics":
        effectiveAtCut(assertion.value.state.effective_at, `${valuePath}.state.effective_at`);
        recordedAtCut(assertion.value.state.recorded_at, `${valuePath}.state.recorded_at`);
        break;
      case "operation_invocation":
        effectiveAtCut(assertion.value.started_at, `${valuePath}.started_at`);
        effectiveAtCut(assertion.value.completed_at, `${valuePath}.completed_at`);
        effectiveAtCut(assertion.value.created_at, `${valuePath}.created_at`);
        break;
      case "lifecycle_validity":
        effectiveAtCut(assertion.value.as_of, `${valuePath}.as_of`);
        break;
      case "engineering_decision":
        effectiveAtCut(assertion.value.timestamp, `${valuePath}.timestamp`);
        effectiveAtCut(assertion.value.as_of, `${valuePath}.as_of`);
        effectiveAtCut(assertion.value.created_at, `${valuePath}.created_at`);
        break;
      case "decision_evidence":
        effectiveAtCut(assertion.value.effective_at, `${valuePath}.effective_at`);
        recordedAtCut(assertion.value.recorded_at, `${valuePath}.recorded_at`);
        break;
      case "state_transition":
        effectiveAtCut(assertion.value.effective_at, `${valuePath}.effective_at`);
        recordedAtCut(assertion.value.recorded_at, `${valuePath}.recorded_at`);
        recordedAtCut(assertion.value.updated_at, `${valuePath}.updated_at`);
        break;
      case "receipt_attestation":
        recordedAtCut(assertion.value.recorded_at, `${valuePath}.recorded_at`);
        break;
      default:
        break;
    }
  });
  return value as ProofTraceDocument;
}

function enforceSemanticPayloadBudgets(document: ProofTraceDocument): void {
  const budget: SemanticPayloadBudget = { nodes: 0, canonicalBytes: 0 };
  document.assertions.forEach((assertion, index) => {
    const path = `$.data.document.assertions[${index}].value`;
    if (assertion.assertion_kind === "artifact_value" && assertion.value.structured_content !== null) {
      semanticJsonValue(assertion.value.structured_content, `${path}.structured_content`, budget);
    } else if (assertion.assertion_kind === "operation_spec_definition") {
      semanticJsonValue(assertion.value.parameter_schema, `${path}.parameter_schema`, budget);
    } else if (assertion.assertion_kind === "operation_parameters") {
      semanticJsonValue(assertion.value.parameters, `${path}.parameters`, budget);
    } else if (assertion.assertion_kind === "operation_environment") {
      semanticJsonValue(assertion.value.environment, `${path}.environment`, budget);
    }
  });
}

function enforceDocumentSizeLimit(document: ProofTraceDocument): number {
  return canonicalJsonByteLength(
    document,
    "$.data.document",
    PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_document_canonical_bytes,
  );
}

export function parseProofTraceEnvelope(value: unknown): ProofTraceEnvelope {
  const item = exactRecord(value, "$", ["schema_version", "request_id", "data", "page", "provenance", "warnings"]);
  literal(item.schema_version, "$.schema_version", ["thread.api.v1"]);
  canonicalUuid(item.request_id, "$.request_id");
  if (item.page !== null) fail("$.page", "expected null for a non-paginated proof");
  const data = exactRecord(item.data, "$.data", ["document", "semantic_identity"]);
  const document = validateDocument(data.document, "$.data.document");
  enforceDocumentSizeLimit(document);
  const identity = exactRecord(data.semantic_identity, "$.data.semantic_identity", [
    "digest", "canonicalizer", "canonicalizer_version", "byte_length", "media_type",
  ]);
  digest(identity.digest, "$.data.semantic_identity.digest");
  literal(identity.canonicalizer, "$.data.semantic_identity.canonicalizer", ["rfc8785"]);
  literal(identity.canonicalizer_version, "$.data.semantic_identity.canonicalizer_version", ["1"]);
  integer(
    identity.byte_length,
    "$.data.semantic_identity.byte_length",
    1,
    PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_document_canonical_bytes,
  );
  literal(identity.media_type, "$.data.semantic_identity.media_type", ["application/vnd.thread.proof-trace.v2+json"]);
  const provenance = exactRecord(item.provenance, "$.provenance", ["source", "projection_version", "schema_migration"]);
  literal(provenance.source, "$.provenance.source", ["thread_core"]);
  literal(provenance.projection_version, "$.provenance.projection_version", [PROOF_TRACE_CONTRACT_VERSION]);
  const schemaMigration = text(provenance.schema_migration, "$.provenance.schema_migration");
  if (!/^\d{14}$/.test(schemaMigration) || schemaMigration < PROOF_TRACE_MIN_SCHEMA_MIGRATION) {
    fail("$.provenance.schema_migration", `expected a migration marker at or after ${PROOF_TRACE_MIN_SCHEMA_MIGRATION}`);
  }
  if (array(item.warnings, "$.warnings").length !== 0) fail("$.warnings", "expected no transport warnings for a semantic proof");
  if (document.snapshot.project_id === "" || document.contract_version !== provenance.projection_version) {
    fail("$.provenance.projection_version", "must match the proof document contract_version");
  }
  return value as ProofTraceEnvelope;
}

function citationIdentityPayload(citation: ProofTraceCitation): JsonValue {
  switch (citation.citation_kind) {
    case "record":
      return {
        citation_kind: citation.citation_kind,
        record_kind: citation.record_kind,
        id: citation.id,
        temporal_basis: citation.temporal_basis,
        effective_at: citation.effective_at,
        recorded_at: citation.recorded_at,
      };
    case "decision_evidence":
      return { citation_kind: citation.citation_kind, decision_id: citation.decision_id, ordinal: citation.ordinal };
    case "operation_registration_receipt":
    case "artifact_invalidation_receipt":
      return {
        citation_kind: citation.citation_kind,
        project_id: citation.project_id,
        idempotency_key: citation.idempotency_key,
      };
  }
}

/**
 * Verify every server-declared SHA identity before a proof can enter UI state.
 * Work is capped at eight WebCrypto operations at a time and observes the
 * request AbortSignal between jobs so navigation cannot retain stale work.
 */
export async function verifyProofTraceEnvelopeIdentities(
  envelope: ProofTraceEnvelope,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  enforceSemanticPayloadBudgets(envelope.data.document);
  enforceDocumentSizeLimit(envelope.data.document);
  throwIfAborted(options.signal);
  const checks: ProofIdentityCheck[] = [
    ...envelope.data.document.citations.map((citation, index) => ({
      path: `$.data.document.citations[${index}].citation_id`,
      expected: citation.citation_id,
      payload: citationIdentityPayload(citation),
    })),
    ...envelope.data.document.assertions.map((assertion, index) => ({
      path: `$.data.document.assertions[${index}].assertion_id`,
      expected: assertion.assertion_id,
      payload: {
        assertion_kind: assertion.assertion_kind,
        subject_citation_id: assertion.subject_citation_id,
        value: assertion.value,
        supporting_citation_ids: assertion.supporting_citation_ids,
      } as unknown as JsonValue,
    })),
    ...envelope.data.document.paths.map((proofPath, index) => ({
      path: `$.data.document.paths[${index}].path_id`,
      expected: proofPath.path_id,
      payload: { steps: proofPath.steps, terminal: proofPath.terminal } as unknown as JsonValue,
    })),
  ];
  await verifyIdentityChecks(checks, options.signal);
  throwIfAborted(options.signal);
  const documentBytes = canonicalBytes(envelope.data.document as unknown as JsonValue);
  if (documentBytes.byteLength !== envelope.data.semantic_identity.byte_length) {
    fail("$.data.semantic_identity.byte_length", "must equal the RFC 8785 proof document byte length");
  }
  const documentDigest = await sha256Bytes(documentBytes);
  if (documentDigest !== envelope.data.semantic_identity.digest) {
    fail("$.data.semantic_identity.digest", "must equal SHA-256 of the RFC 8785 proof document");
  }
  throwIfAborted(options.signal);
}

function normalizedInstantMicros(value: string, path: string): bigint {
  if (!validAwareTimestampComponents(value)) fail(path, "expected a valid aware instant");
  const match = AWARE_TIMESTAMP_PARTS.exec(value)!;
  const wholeSecond = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}${match[8]}`;
  const wholeSecondMillis = Date.parse(wholeSecond);
  if (!Number.isFinite(wholeSecondMillis)) fail(path, "expected a valid aware instant");
  const micros = BigInt(`${match[7] ?? ""}000000`.slice(0, 6));
  return BigInt(wholeSecondMillis) * 1000n + micros;
}

function validateProofTraceResponseRequestBinding(envelope: ProofTraceEnvelope, request: ProofTraceRequest): void {
  const snapshot = envelope.data.document.snapshot;
  if (snapshot.project_id.toLowerCase() !== request.project_id.toLowerCase()) {
    fail("$.data.document.snapshot.project_id", "must equal request.project_id");
  }
  if (snapshot.root_ref.entity_kind !== request.root_ref.entity_kind
    || snapshot.root_ref.id.toLowerCase() !== request.root_ref.id.toLowerCase()) {
    fail("$.data.document.snapshot.root_ref", "must equal the exact requested ArtifactVersion");
  }
  const expectedLimits: ProofTraceLimits = {
    max_depth: request.max_depth ?? PROOF_TRACE_DEFAULT_LIMITS.max_depth,
    max_records: request.max_records ?? PROOF_TRACE_DEFAULT_LIMITS.max_records,
    max_paths: request.max_paths ?? PROOF_TRACE_DEFAULT_LIMITS.max_paths,
    max_path_steps: request.max_path_steps ?? PROOF_TRACE_DEFAULT_LIMITS.max_path_steps,
  };
  for (const key of ["max_depth", "max_records", "max_paths", "max_path_steps"] as const) {
    if (snapshot.limits[key] !== expectedLimits[key]) {
      fail(`$.data.document.snapshot.limits.${key}`, `must equal the requested/defaulted ${key}`);
    }
  }
  if (typeof request.as_of === "string"
    && normalizedInstantMicros(snapshot.effective_as_of, "$.data.document.snapshot.effective_as_of")
      !== normalizedInstantMicros(request.as_of, "request.as_of")) {
    fail("$.data.document.snapshot.effective_as_of", "must equal the requested as_of instant");
  }
  if (request.recorded_as_of !== undefined && snapshot.recorded_as_of !== request.recorded_as_of) {
    fail("$.data.document.snapshot.recorded_as_of", "must equal request.recorded_as_of");
  }
  if (request.snapshot_token !== undefined && snapshot.snapshot_token !== request.snapshot_token) {
    fail("$.data.document.snapshot.snapshot_token", "must equal request.snapshot_token");
  }
}

export async function fetchProofTrace(
  request: ProofTraceRequest,
  options: { signal?: AbortSignal } = {},
): Promise<ProofTraceEnvelope> {
  const normalizedRequest = normalizeProofTraceRequest(request);
  const value = await apiClient<unknown>(PROOF_TRACE_PATH, {
    method: "POST",
    body: normalizedRequest,
    signal: options.signal,
  });
  const envelope = parseProofTraceEnvelope(value);
  validateProofTraceResponseRequestBinding(envelope, normalizedRequest);
  await verifyProofTraceEnvelopeIdentities(envelope, options);
  return envelope;
}

function exportFilename(response: Response, request: ProofTraceExportRequest, format: ProofTraceExportFormat): string {
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"\\/]+)"/i.exec(disposition);
  return match?.[1] ?? `proof-trace-${request.root_ref.id}.${format}`;
}

async function errorDetail(response: Response): Promise<string> {
  try {
    const value = await response.clone().json() as { error?: { message?: unknown }; detail?: unknown };
    if (typeof value.error?.message === "string") return value.error.message;
    if (typeof value.detail === "string") return value.detail;
  } catch {
    // The export endpoint may return a non-JSON proxy error.
  }
  return response.text().catch(() => "Unknown error");
}

export async function exportProofTrace(
  request: ProofTraceExportRequest,
  format: ProofTraceExportFormat,
  options: { signal?: AbortSignal } = {},
): Promise<ProofTraceExport> {
  validateProofTraceExportRequest(request);
  const normalizedRequest: ProofTraceExportRequest = {
    ...request,
    as_of: normalizeInstantToUtcMicroseconds(request.as_of),
    project_id: request.project_id.toLowerCase(),
    root_ref: {
      entity_kind: "artifact_version",
      id: request.root_ref.id.toLowerCase(),
    },
  };
  literal(format, "format", ["json", "html"]);
  const token = await getAuthToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const apiBase = import.meta.env.VITE_API_URL ?? "/api";
  const response = await fetch(`${apiBase}${PROOF_TRACE_EXPORT_PATH}?format=${format}`, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(normalizedRequest),
    signal: options.signal,
  });
  if (response.status === 409) throw new ProofTraceChangedError();
  if (!response.ok) throw new Error(`API ${response.status}: ${await errorDetail(response)}`);
  const expectedMediaType = format === "json" ? "application/vnd.thread.proof-trace.v2+json" : "text/html";
  const mediaType = response.headers.get("Content-Type") ?? "";
  if (!hasExactMediaType(mediaType, expectedMediaType)) {
    throw new ProofTraceValidationError("response.Content-Type", `expected ${expectedMediaType}`);
  }
  const responseDigest = response.headers.get("X-Thread-Proof-Digest");
  if (responseDigest !== normalizedRequest.expected_semantic_digest) {
    throw new ProofTraceValidationError("response.X-Thread-Proof-Digest", "must match the displayed semantic digest");
  }
  let bytes: Uint8Array<ArrayBuffer>;
  try {
    bytes = await readBoundedResponseBytes(
      response,
      format === "json"
        ? PROOF_TRACE_SEMANTIC_PAYLOAD_LIMITS.maximum_document_canonical_bytes
        : PROOF_TRACE_MAX_HTML_BYTES,
    );
    if (format === "json") {
      await verifyJsonExportBytes(bytes, normalizedRequest.expected_semantic_digest);
    } else {
      verifyOfflineHtmlBytes(bytes, [
        "<h1>Thread proof trace</h1>",
        normalizedRequest.expected_semantic_digest,
        normalizedRequest.project_id,
        normalizedRequest.root_ref.id,
        normalizedRequest.as_of,
        normalizedRequest.recorded_as_of,
        normalizedRequest.snapshot_token,
      ]);
    }
  } catch (error) {
    if (error instanceof ExportIntegrityError) {
      throw new ProofTraceValidationError("response.body", error.message);
    }
    throw error;
  }
  return {
    blob: exportBlob(bytes, mediaType),
    filename: exportFilename(response, normalizedRequest, format),
    mediaType,
    semanticDigest: responseDigest,
  };
}
